import React, { useState, useRef, useEffect, useCallback } from 'react';
import  './App.css';

declare const process: { env: { REACT_APP_CHECKOUT_URL?: string; REACT_APP_CHECKOUT_URL_PRO?: string; REACT_APP_DEV_BYPASS?: string } };
// ── Types ──────────────────────────────────────────────────────────────────────
type CopyTone = 'clear' | 'bold' | 'friendly' | 'premium' | 'playful';
type MessageGoal = 'benefit' | 'feature' | 'problem' | 'trust' | 'cta';

interface Screenshot {
  id: string;
  file: File;
  dataUrl: string;
  headline: string;
  body: string;
  featureName: string;
  benefit: string;
  messageGoal: MessageGoal;
  gradientStart: string;
  gradientEnd: string;
  textColor: string;
  headlineFontFamily: string;
  headlineFontSize: number;
  headlineFontWeight: string;
  headlineFontItalic: boolean;
  bodyFontFamily: string;
  bodyFontSize: number;
  bodyFontWeight: string;
  textAlign: 'left' | 'center' | 'right';
  textPosition: 'top' | 'middle' | 'bottom';
  textShadow: boolean;
  letterSpacing: number;
  showDeviceFrame: boolean;
}

type DeviceKey = 'iphone-6.9' | 'iphone-6.5' | 'ipad-13';
type OutputFormat = 'png' | 'jpg';
type VideoOrientation = 'portrait' | 'landscape';
type VideoFit = 'contain' | 'cover';

interface VideoInfo { duration: number; w: number; h: number; }

interface FFmpegWorkerMessage {
  id?: number;
  type: 'done' | 'error' | 'progress' | 'log';
  data?: unknown;
  message?: string;
  progress?: number;
  time?: number;
}

interface FFmpegWorkerClient {
  worker: Worker;
  request: (type: string, payload?: Record<string, unknown>, transfer?: Transferable[]) => Promise<unknown>;
}
interface GradientPreset { name: string; start: string; end: string; }
interface FontFamily { label: string; value: string; group: string; }


type AppSection = 'dashboard' | 'screenshots' | 'video' | 'preflight' | 'projects' | 'pricing';
type VideoFitMode = 'contain' | 'cover';
type CheckStatus = 'pass' | 'warn' | 'fail' | 'info';

interface VideoPreset {
  key: string;
  label: string;
  platform: string;
  width: number;
  height: number;
  note: string;
}

interface ValidationItem {
  label: string;
  status: CheckStatus;
  detail: string;
}

interface PersistedScreenshot extends Omit<Screenshot, 'file'> {
  fileName: string;
}

interface SavedProject {
  id: string;
  name: string;
  updatedAt: number;
  appAudience?: string;
  corePromise?: string;
  copyTone?: CopyTone;
  deviceSize: DeviceKey;
  outputFormat: OutputFormat;
  jpgQuality: number;
  screenshots: PersistedScreenshot[];
  videoPresetKey: string;
  videoFit: VideoFitMode;
  trimStart: number;
  trimEnd: number;
  posterTime: number;
}

interface ExportResult {
  url: string;
  fileName: string;
  blob: Blob;
  width: number;
  height: number;
  duration: number;
  createdAt: number;
}

// ── JSZip (loaded on demand from CDN) ─────────────────────────────────────────
declare const JSZip: any;

// ── Constants ──────────────────────────────────────────────────────────────────
const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?' +
  'family=Inter:wght@400;600;700&family=Poppins:wght@400;600;700;900&' +
  'family=Montserrat:wght@400;600;700;900&family=Nunito:wght@400;700;900&' +
  'family=DM+Sans:wght@400;600;700&family=Outfit:wght@400;600;700;900&' +
  'family=Bebas+Neue&family=Anton&family=Oswald:wght@400;600;700&' +
  'family=Barlow+Condensed:wght@400;600;700;900&' +
  'family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&' +
  'family=Lora:ital,wght@0,400;0,700;1,400;1,700&' +
  'family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,700&' +
  'family=DM+Serif+Display:ital@0;1&family=Libre+Baskerville:wght@400;700&' +
  'family=Great+Vibes&family=Pacifico&family=Dancing+Script:wght@400;700&' +
  'family=Satisfy&family=Pinyon+Script&family=Alex+Brush&family=Italianno&' +
  'family=Caveat:wght@400;700&family=Kalam:wght@400;700&family=Patrick+Hand&' +
  'family=Architects+Daughter&family=Righteous&family=Fredoka:wght@400;600;700&' +
  'family=Comfortaa:wght@400;700&display=swap';

const FONT_FAMILIES: FontFamily[] = [
  { label: '🔵 SF Pro (System)', value: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif', group: 'Sans-Serif' },
  { label: '🔵 Inter',           value: '"Inter", sans-serif',                 group: 'Sans-Serif' },
  { label: '🔵 Poppins',         value: '"Poppins", sans-serif',               group: 'Sans-Serif' },
  { label: '🔵 Montserrat',      value: '"Montserrat", sans-serif',            group: 'Sans-Serif' },
  { label: '🔵 Nunito',          value: '"Nunito", sans-serif',                group: 'Sans-Serif' },
  { label: '🔵 DM Sans',         value: '"DM Sans", sans-serif',               group: 'Sans-Serif' },
  { label: '🔵 Outfit',          value: '"Outfit", sans-serif',                group: 'Sans-Serif' },
  { label: '⚡ Bebas Neue',      value: '"Bebas Neue", Impact, sans-serif',    group: 'Display' },
  { label: '⚡ Anton',           value: '"Anton", Impact, sans-serif',         group: 'Display' },
  { label: '⚡ Oswald',          value: '"Oswald", sans-serif',                group: 'Display' },
  { label: '⚡ Barlow Condensed',value: '"Barlow Condensed", sans-serif',      group: 'Display' },
  { label: '📖 Playfair Display',value: '"Playfair Display", Georgia, serif',  group: 'Serif' },
  { label: '📖 Cormorant',       value: '"Cormorant Garamond", Georgia, serif',group: 'Serif' },
  { label: '📖 DM Serif Display',value: '"DM Serif Display", Georgia, serif',  group: 'Serif' },
  { label: '📖 Lora',            value: '"Lora", Georgia, serif',              group: 'Serif' },
  { label: '📖 Libre Baskerville',value:'"Libre Baskerville", Georgia, serif', group: 'Serif' },
  { label: '✍️ Great Vibes',     value: '"Great Vibes", cursive',              group: 'Script' },
  { label: '✍️ Pinyon Script',   value: '"Pinyon Script", cursive',            group: 'Script' },
  { label: '✍️ Alex Brush',      value: '"Alex Brush", cursive',               group: 'Script' },
  { label: '✍️ Italianno',       value: '"Italianno", cursive',                group: 'Script' },
  { label: '✍️ Satisfy',         value: '"Satisfy", cursive',                  group: 'Script' },
  { label: '✍️ Pacifico',        value: '"Pacifico", cursive',                 group: 'Script' },
  { label: '✍️ Dancing Script',  value: '"Dancing Script", cursive',           group: 'Script' },
  { label: '✏️ Caveat',          value: '"Caveat", cursive',                   group: 'Handwritten' },
  { label: '✏️ Kalam',           value: '"Kalam", cursive',                    group: 'Handwritten' },
  { label: '✏️ Patrick Hand',    value: '"Patrick Hand", cursive',             group: 'Handwritten' },
  { label: '✏️ Architects Daughter', value: '"Architects Daughter", cursive',  group: 'Handwritten' },
  { label: '🎨 Righteous',       value: '"Righteous", sans-serif',             group: 'Fun' },
  { label: '🎨 Fredoka',         value: '"Fredoka", sans-serif',               group: 'Fun' },
  { label: '🎨 Comfortaa',       value: '"Comfortaa", sans-serif',             group: 'Fun' },
];

const FONT_WEIGHTS = [
  { label: 'Regular (400)',  value: '400' },
  { label: 'SemiBold (600)', value: '600' },
  { label: 'Bold (700)',     value: '700' },
  { label: 'Black (900)',    value: '900' },
];

const DEFAULT_TYPOGRAPHY: Omit<Screenshot,'id'|'file'|'dataUrl'|'headline'|'body'|'featureName'|'benefit'|'messageGoal'|'gradientStart'|'gradientEnd'|'textColor'> = {
  headlineFontFamily: '"Great Vibes", cursive',
  headlineFontSize: 1.5,
  headlineFontWeight: '400',
  headlineFontItalic: false,
  bodyFontFamily: '"Poppins", sans-serif',
  bodyFontSize: 0.85,
  bodyFontWeight: '400',
  textAlign: 'center',
  textPosition: 'top',
  textShadow: false,
  letterSpacing: 0,
  showDeviceFrame: true,
};

const COPY_TONES: { value: CopyTone; label: string; description: string }[] = [
  { value: 'clear', label: 'Clear', description: 'Direct, specific and easy to scan' },
  { value: 'bold', label: 'Bold', description: 'Confident, energetic and benefit-led' },
  { value: 'friendly', label: 'Friendly', description: 'Warm, conversational and approachable' },
  { value: 'premium', label: 'Premium', description: 'Polished, calm and aspirational' },
  { value: 'playful', label: 'Playful', description: 'Light, memorable and informal' },
];

const MESSAGE_GOALS: { value: MessageGoal; label: string }[] = [
  { value: 'benefit', label: 'Lead with the benefit' },
  { value: 'feature', label: 'Explain the feature' },
  { value: 'problem', label: 'Solve a pain point' },
  { value: 'trust', label: 'Build trust' },
  { value: 'cta', label: 'Encourage action' },
];

const GRADIENT_PRESETS: GradientPreset[] = [
  { name: '🫒 Olive',      start: '#697565', end: '#4A5240' },
  { name: '🍷 Burgundy',   start: '#7C3238', end: '#5A1F23' },
  { name: '🌿 Sage',       start: '#87A878', end: '#6B8E5F' },
  { name: '🌊 Teal',       start: '#3D7B7F', end: '#2A5456' },
  { name: '☕ Mocha',      start: '#7B6B5D', end: '#5C4F43' },
  { name: '🍇 Plum',       start: '#6B4E71', end: '#4F3654' },
  { name: '🌲 Forest',     start: '#4A6741', end: '#354B2F' },
  { name: '🪨 Slate',      start: '#5B6670', end: '#414B54' },
  { name: '🌅 Terracotta', start: '#B85C4D', end: '#8F4538' },
  { name: '🫐 Navy',       start: '#2C3E50', end: '#1A252F' },
  { name: '🍂 Rust',       start: '#A05344', end: '#7A3E32' },
  { name: '🌸 Mauve',      start: '#9B7E9D', end: '#7A6179' },
  { name: '🫒 Lt Olive',   start: '#B5BFA9', end: '#9CAA8C' },
  { name: '🍷 Lt Burg',    start: '#C89B9D', end: '#B17A7C' },
  { name: '🌿 Lt Sage',    start: '#C8DEB9', end: '#B0CCA0' },
  { name: '🌊 Lt Teal',    start: '#9BC4C7', end: '#7FADB0' },
  { name: '☕ Lt Mocha',   start: '#C9BDB0', end: '#B3A596' },
  { name: '🍇 Lt Plum',    start: '#BFA9C4', end: '#A791AC' },
  { name: '🌲 Lt Forest',  start: '#A7C199', end: '#8FAA7E' },
  { name: '🪨 Lt Slate',   start: '#A8B3BC', end: '#8F9BA6' },
  { name: '🌅 Lt Terra',   start: '#E5AB9F', end: '#D68E7F' },
  { name: '🫐 Lt Navy',    start: '#7B8FA3', end: '#5F7485' },
  { name: '🍂 Lt Rust',    start: '#D19A88', end: '#BD806D' },
  { name: '🌸 Lt Mauve',   start: '#D4BFD6', end: '#BCA5BE' },
];

const DEVICE_SIZES: Record<DeviceKey, { width: number; height: number; name: string }> = {
  'iphone-6.9': { width: 1260, height: 2736, name: 'iPhone 6.9"' },
  'iphone-6.5': { width: 1284, height: 2778, name: 'iPhone 6.5"' },
  'ipad-13':    { width: 2064, height: 2752, name: 'iPad 13"' },
};


const VIDEO_PRESETS: VideoPreset[] = [
  { key: 'iphone-modern-portrait', label: 'iPhone modern portrait', platform: 'iPhone', width: 886, height: 1920, note: 'Accepted for current 6.9, 6.5, 6.3 and 6.1 inch displays' },
  { key: 'iphone-modern-landscape', label: 'iPhone modern landscape', platform: 'iPhone', width: 1920, height: 886, note: 'Accepted for current 6.9, 6.5, 6.3 and 6.1 inch displays' },
  { key: 'iphone-legacy-portrait', label: 'iPhone legacy portrait', platform: 'iPhone', width: 1080, height: 1920, note: 'Accepted for legacy 5.5 and 4 inch display groups' },
  { key: 'iphone-legacy-landscape', label: 'iPhone legacy landscape', platform: 'iPhone', width: 1920, height: 1080, note: 'Accepted for legacy 5.5 and 4 inch display groups' },
  { key: 'ipad-portrait', label: 'iPad portrait', platform: 'iPad', width: 1200, height: 1600, note: 'Accepted for current 13 and 11 inch iPad display groups' },
  { key: 'ipad-landscape', label: 'iPad landscape', platform: 'iPad', width: 1600, height: 1200, note: 'Accepted for current 13 and 11 inch iPad display groups' },
  { key: 'mac-landscape', label: 'Mac landscape', platform: 'Mac', width: 1920, height: 1080, note: 'Required landscape size for Mac app previews' },
];

const PROJECT_DB_NAME = 'appready-studio-db';
const PROJECT_STORE_NAME = 'projects';
const CHECKOUT_URL_LAUNCH = process.env.REACT_APP_CHECKOUT_URL || 'https://appready-studio.lemonsqueezy.com/checkout/buy/da57775b-4807-4621-a9dd-13b9f5f5fcbd';
const CHECKOUT_URL_PRO = process.env.REACT_APP_CHECKOUT_URL_PRO || 'https://appready-studio.lemonsqueezy.com/checkout/buy/b7c3fc21-9fe5-4b92-84e2-2d2152301368';
const ENTITLEMENT_WORKER = 'https://appready-entitlements.mtobias2000.workers.dev';

function openProjectDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(PROJECT_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PROJECT_STORE_NAME)) {
        db.createObjectStore(PROJECT_STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Could not open project database'));
  });
}

async function putSavedProject(project: SavedProject): Promise<void> {
  const db = await openProjectDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(PROJECT_STORE_NAME, 'readwrite');
    tx.objectStore(PROJECT_STORE_NAME).put(project);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('Could not save project'));
  });
  db.close();
}

async function getSavedProjects(): Promise<SavedProject[]> {
  const db = await openProjectDb();
  const projects = await new Promise<SavedProject[]>((resolve, reject) => {
    const tx = db.transaction(PROJECT_STORE_NAME, 'readonly');
    const request = tx.objectStore(PROJECT_STORE_NAME).getAll();
    request.onsuccess = () => resolve((request.result as SavedProject[]).sort((a, b) => b.updatedAt - a.updatedAt));
    request.onerror = () => reject(request.error || new Error('Could not read projects'));
  });
  db.close();
  return projects;
}

async function removeSavedProject(id: string): Promise<void> {
  const db = await openProjectDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(PROJECT_STORE_NAME, 'readwrite');
    tx.objectStore(PROJECT_STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('Could not delete project'));
  });
  db.close();
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r
    ? { r: parseInt(r[1]!, 16) / 255, g: parseInt(r[2]!, 16) / 255, b: parseInt(r[3]!, 16) / 255 }
    : { r: 0, g: 0, b: 0 };
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
): void {
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let cur = words[0] ?? '';
  for (let i = 1; i < words.length; i++) {
    const w = words[i]!;
    if (ctx.measureText(cur + ' ' + w).width < maxWidth) cur += ' ' + w;
    else { lines.push(cur); cur = w; }
  }
  lines.push(cur);
  return lines;
}


// Free-plan video watermark, drawn on a canvas and burned in with FFmpeg's overlay filter.
// (drawtext needs fonts/fontconfig, which the ffmpeg.wasm core does not ship with.)
async function makeWatermarkPng(w: number, h: number): Promise<Uint8Array> {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable for watermark');
  const text = 'AppReady Studio - Free Plan';
  const drawBanner = (size: number, y: number, alpha: number) => {
    ctx.font = `700 ${size}px -apple-system, BlinkMacSystemFont, Arial, sans-serif`;
    const tw = ctx.measureText(text).width;
    const pad = size * 0.6;
    ctx.globalAlpha = alpha * 0.7;
    ctx.fillStyle = '#000000';
    ctx.fillRect((w - tw) / 2 - pad, y - size / 2 - pad / 2, tw + pad * 2, size + pad);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, w / 2, y);
  };
  drawBanner(Math.round(h * 0.03), h * 0.5, 0.65);
  drawBanner(Math.round(h * 0.025), h * 0.3, 0.4);
  drawBanner(Math.round(h * 0.025), h * 0.7, 0.4);
  ctx.globalAlpha = 1;
  const blob = await new Promise<Blob>((resolve, reject) =>
    c.toBlob(b => (b ? resolve(b) : reject(new Error('Could not create watermark image'))), 'image/png'));
  return new Uint8Array(await blob.arrayBuffer());
}

function humanizeFileName(fileName: string): string {
  const base = fileName
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b(copy|final|export|edited)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!base || /^(img|image|screenshot|screen shot|simulator screen shot)(\s*\d.*)?$/i.test(base)) {
    return '';
  }

  return base
    .split(' ')
    .map(word => word.length <= 3 ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function lowerFirst(value: string): string {
  const trimmed = value.trim().replace(/[.!?]+$/, '');
  return trimmed ? trimmed.charAt(0).toLowerCase() + trimmed.slice(1) : '';
}

function titleText(value: string): string {
  const trimmed = value.trim().replace(/[.!?]+$/, '');
  return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : '';
}

function createScreenshotCopy(
  screenshot: Screenshot,
  appName: string,
  audience: string,
  corePromise: string,
  tone: CopyTone,
): Pick<Screenshot, 'headline' | 'body'> {
  const cleanAppName = appName.trim() || 'Your app';
  const cleanAudience = audience.trim() || 'your users';
  const feature = screenshot.featureName.trim() || 'This feature';
  const benefit = screenshot.benefit.trim() || corePromise.trim() || `get more from ${cleanAppName}`;
  const benefitLower = lowerFirst(benefit);
  const featureLower = lowerFirst(feature);

  const toneHeadlines: Record<CopyTone, Record<MessageGoal, string>> = {
    clear: {
      benefit: titleText(benefit),
      feature: `${feature}, made simple`,
      problem: `A simpler way to ${benefitLower}`,
      trust: `Built for ${cleanAudience}`,
      cta: `Start ${benefitLower} today`,
    },
    bold: {
      benefit: `${titleText(benefit)}, simply.`,
      feature: `${feature}, front and center`,
      problem: `Stop struggling to ${benefitLower}`,
      trust: `Made for ${cleanAudience}`,
      cta: `Make the switch today`,
    },
    friendly: {
      benefit: `Meet your easier way to ${benefitLower}`,
      feature: `Say hello to ${featureLower}`,
      problem: `Less hassle. More ${benefitLower}.`,
      trust: `Made with ${cleanAudience} in mind`,
      cta: `Ready to make life easier?`,
    },
    premium: {
      benefit: `A smarter way to ${benefitLower}`,
      feature: `${feature}, thoughtfully designed`,
      problem: `Leave the friction behind`,
      trust: `Designed around ${cleanAudience}`,
      cta: `Experience a better way`,
    },
    playful: {
      benefit: `${titleText(benefit)}, minus the hassle`,
      feature: `${feature} that just works`,
      problem: `Because ${benefitLower} should not be hard`,
      trust: `Made for real ${cleanAudience}`,
      cta: `Give it a try`,
    },
  };

  const bodyByGoal: Record<MessageGoal, string> = {
    benefit: `${cleanAppName} helps ${cleanAudience} ${benefitLower} with a clear, simple workflow.`,
    feature: `Use ${featureLower} to ${benefitLower}, all in one clear and simple workflow.`,
    problem: `A simpler way to ${benefitLower}.`,
    trust: `${cleanAppName} is designed to help ${cleanAudience} ${benefitLower} without unnecessary complexity.`,
    cta: `Open ${cleanAppName} and use ${featureLower} to ${benefitLower} today.`,
  };

  return {
    headline: toneHeadlines[tone][screenshot.messageGoal].slice(0, 60),
    body: bodyByGoal[screenshot.messageGoal].slice(0, 150),
  };
}

// ── TrimScrubber ───────────────────────────────────────────────────────────────
interface TrimScrubberProps {
  duration: number;
  trimStart: number;
  trimEnd: number;
  onTrimChange: (start: number, end: number) => void;
  videoSrc: string | null;
}

function TrimScrubber({ duration, trimStart, trimEnd, onTrimChange, videoSrc }: TrimScrubberProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<HTMLCanvasElement[]>([]);
  const [thumbUrls, setThumbUrls] = useState<string[]>([]);
  const dragging = useRef<'start' | 'end' | null>(null);

  const THUMB_COUNT = 10;

  // Generate thumbnail strip whenever videoSrc changes
  useEffect(() => {
    if (!videoSrc || !duration) return;
    let cancelled = false;
    const urls: string[] = [];
    const vid = document.createElement('video');
    vid.src = videoSrc;
    vid.muted = true;
    vid.preload = 'auto';

    const canvas = document.createElement('canvas');
    canvas.width = 80;
    canvas.height = 45;
    const ctx = canvas.getContext('2d')!;

    const captureFrame = (index: number): Promise<string> =>
      new Promise(resolve => {
        const t = (index / (THUMB_COUNT - 1)) * duration;
        vid.currentTime = Math.min(t, duration - 0.05);
        vid.onseeked = () => {
          ctx.drawImage(vid, 0, 0, 80, 45);
          resolve(canvas.toDataURL('image/jpeg', 0.5));
        };
      });

    vid.onloadedmetadata = async () => {
      for (let i = 0; i < THUMB_COUNT; i++) {
        if (cancelled) break;
        const url = await captureFrame(i);
        urls.push(url);
      }
      if (!cancelled) setThumbUrls([...urls]);
      vid.removeAttribute('src');
      vid.load();
    };

    return () => { cancelled = true; };
  }, [videoSrc, duration]);

  const pct = (t: number) => `${((t / duration) * 100).toFixed(2)}%`;

  const getTimeFromPointer = (e: React.PointerEvent | PointerEvent): number => {
    const rail = railRef.current;
    if (!rail) return 0;
    const rect = rail.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    return ratio * duration;
  };

  const onPointerDownStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragging.current = 'start';
  };
  const onPointerDownEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragging.current = 'end';
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const t = getTimeFromPointer(e);
    if (dragging.current === 'start') {
      onTrimChange(Math.min(t, trimEnd - 1), trimEnd);
    } else {
      onTrimChange(trimStart, Math.max(t, trimStart + 1));
    }
  };
  const onPointerUp = () => { dragging.current = null; };

  const railStyle: React.CSSProperties = {
    position: 'relative', height: 52, borderRadius: 8, overflow: 'hidden',
    background: '#111', cursor: 'crosshair', userSelect: 'none',
  };

  const handleStyle = (side: 'start' | 'end'): React.CSSProperties => ({
    position: 'absolute',
    top: 0, bottom: 0,
    left: side === 'start' ? `calc(${pct(trimStart)} - 10px)` : undefined,
    right: side === 'end' ? `calc(${pct(1 - trimEnd / duration)} - 10px)` : undefined,
    width: 20,
    background: '#5C6BC0',
    borderRadius: side === 'start' ? '6px 0 0 6px' : '0 6px 6px 0',
    cursor: 'ew-resize',
    zIndex: 3,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontSize: 11, fontWeight: 900,
    boxShadow: '0 0 0 2px rgba(255,255,255,0.3)',
  });

  return (
    <div>
      <div
        ref={railRef}
        style={railStyle}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* Thumbnail strip */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
          {thumbUrls.length > 0
            ? thumbUrls.map((url, i) => (
                <img key={i} src={url} alt="" style={{ flex: 1, objectFit: 'cover', height: '100%', display: 'block' }} />
              ))
            : Array.from({ length: THUMB_COUNT }).map((_, i) => (
                <div key={i} style={{ flex: 1, background: i % 2 ? '#1a1a1a' : '#222', height: '100%' }} />
              ))
          }
        </div>

        {/* Dark overlay outside trim window */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1 }} />
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: pct(trimStart), width: `calc(${pct(trimEnd)} - ${pct(trimStart)})`,
          background: 'transparent', border: '3px solid #5C6BC0',
          zIndex: 2, pointerEvents: 'none',
        }} />

        {/* Handles */}
        <div style={handleStyle('start')} onPointerDown={onPointerDownStart}>⠿</div>
        <div style={handleStyle('end')} onPointerDown={onPointerDownEnd}>⠿</div>

        {/* Time labels */}
        <div style={{ position: 'absolute', bottom: 2, left: `${(trimStart / duration) * 100}%`, transform: 'translateX(12px)', fontSize: 9, color: '#fff', fontWeight: 700, zIndex: 4, pointerEvents: 'none', background: 'rgba(0,0,0,0.5)', padding: '1px 4px', borderRadius: 3 }}>
          {trimStart.toFixed(1)}s
        </div>
        <div style={{ position: 'absolute', bottom: 2, left: `${(trimEnd / duration) * 100}%`, transform: 'translateX(-100%) translateX(-12px)', fontSize: 9, color: '#fff', fontWeight: 700, zIndex: 4, pointerEvents: 'none', background: 'rgba(0,0,0,0.5)', padding: '1px 4px', borderRadius: 3 }}>
          {trimEnd.toFixed(1)}s
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 11, color: '#7b8498' }}>
        <span>0:00</span>
        <span style={{ fontWeight: 800, color: (trimEnd - trimStart) >= 15 && (trimEnd - trimStart) <= 30 ? '#15803d' : '#b91c1c' }}>
          {(trimEnd - trimStart).toFixed(1)}s selected
        </span>
        <span>{Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, '0')}</span>
      </div>
    </div>
  );
}

// ── LivePreview ────────────────────────────────────────────────────────────────
interface LivePreviewProps {
  screenshot: Screenshot;
  deviceSize: DeviceKey;
}

function LivePreview({ screenshot: s, deviceSize }: LivePreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { width, height } = DEVICE_SIZES[deviceSize];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !s.dataUrl) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = width;
    canvas.height = height;

    const img = new Image();
    img.onload = () => {
      // Background gradient
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      const hexToRgbLocal = (hex: string) => {
        const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return r ? { r: parseInt(r[1]!, 16), g: parseInt(r[2]!, 16), b: parseInt(r[3]!, 16) } : { r: 0, g: 0, b: 0 };
      };
      const c1 = hexToRgbLocal(s.gradientStart);
      const c2 = hexToRgbLocal(s.gradientEnd);
      grad.addColorStop(0, `rgb(${c1.r},${c1.g},${c1.b})`);
      grad.addColorStop(1, `rgb(${c2.r},${c2.g},${c2.b})`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // Image sizing
      const textZone = height * 0.22;
      const maxImageHeight = height * 0.73;
      const maxImageWidth = width * 0.88;
      const frameScale = s.showDeviceFrame ? 0.92 : 1;
      const scale = Math.min(maxImageWidth * frameScale / img.naturalWidth, maxImageHeight * frameScale / img.naturalHeight);
      const scaledWidth = img.naturalWidth * scale;
      const scaledHeight = img.naturalHeight * scale;

      const textY = textZone * 0.18;

      ctx.textAlign = s.textAlign as CanvasTextAlign;
      const textX = s.textAlign === 'left' ? width * 0.07 : s.textAlign === 'right' ? width * 0.93 : width / 2;
      const maxTextWidth = width * 0.86;

      const applyShadow = (on: boolean) => {
        ctx.shadowColor = on ? 'rgba(0,0,0,0.5)' : 'transparent';
        ctx.shadowBlur = on ? 12 : 0;
        ctx.shadowOffsetY = on ? 3 : 0;
      };
      const drawTracked = (text: string, x: number, y: number, spacing: number) => {
        if (!spacing) { ctx.fillText(text, x, y); return; }
        const chars = text.split('');
        let cx = x;
        if (s.textAlign === 'center') cx = x - ctx.measureText(text).width / 2 - spacing * (chars.length - 1) / 2;
        if (s.textAlign === 'right') cx = x - ctx.measureText(text).width - spacing * (chars.length - 1);
        const saved = ctx.textAlign;
        ctx.textAlign = 'left';
        chars.forEach(ch => { ctx.fillText(ch, cx, y); cx += ctx.measureText(ch).width + spacing; });
        ctx.textAlign = saved;
      };
      const wrapTextLocal = (text: string, maxW: number): string[] => {
        const words = text.split(' ');
        const lines: string[] = [];
        let cur = words[0] ?? '';
        for (let i = 1; i < words.length; i++) {
          const w = words[i]!;
          if (ctx.measureText(cur + ' ' + w).width < maxW) cur += ' ' + w;
          else { lines.push(cur); cur = w; }
        }
        lines.push(cur);
        return lines;
      };

      const headlinePx = Math.round((width / 18) * s.headlineFontSize);
      ctx.font = `${s.headlineFontItalic ? 'italic ' : ''}${s.headlineFontWeight} ${headlinePx}px ${s.headlineFontFamily}`;
      ctx.fillStyle = s.textColor;
      ctx.textBaseline = 'top';
      applyShadow(s.textShadow);
      const headlineLines = wrapTextLocal(s.headline || '', maxTextWidth);
      const lineH = headlinePx * 1.25;

      // Measure actual text block height before placing the image
      const bodyPx = s.body ? Math.round((width / 36) * s.bodyFontSize) : 0;
      let bodyLines: string[] = [];
      if (s.body) {
        ctx.font = `${s.bodyFontWeight} ${bodyPx}px ${s.bodyFontFamily}`;
        bodyLines = wrapTextLocal(s.body, maxTextWidth);
      }
      const headlineBlockH = headlineLines.length * lineH;
      const bodyBlockH = s.body ? (headlinePx * 0.55 + bodyLines.length * bodyPx * 1.5) : 0;
      const totalTextH = headlineBlockH + bodyBlockH;

      // imageY: for top position use actual text height so image never overlaps text
      let imageY = s.textPosition === 'bottom'
        ? 30
        : s.textPosition === 'middle'
          ? textZone + (height - textZone - scaledHeight) / 2
          : Math.max(textZone + 30, textY + totalTextH + height * 0.04);

      // For bottom position, re-anchor textY below the image
      const resolvedTextY = s.textPosition === 'bottom' ? 30 + scaledHeight + 40 : textY;

      // Draw headline
      ctx.font = `${s.headlineFontItalic ? 'italic ' : ''}${s.headlineFontWeight} ${headlinePx}px ${s.headlineFontFamily}`;
      headlineLines.forEach((line, i) => drawTracked(line, textX, resolvedTextY + i * lineH, s.letterSpacing));

      if (s.body) {
        ctx.font = `${s.bodyFontWeight} ${bodyPx}px ${s.bodyFontFamily}`;
        ctx.globalAlpha = 0.88;
        const bodyY = resolvedTextY + headlineLines.length * lineH + headlinePx * 0.55;
        bodyLines.forEach((line, i) => drawTracked(line, textX, bodyY + i * bodyPx * 1.5, s.letterSpacing * 0.5));
        ctx.globalAlpha = 1;
      }
      applyShadow(false);

      const imageX = (width - scaledWidth) / 2;
      if (s.showDeviceFrame) {
        const ft = width * 0.025;
        const br = width * 0.1;
        ctx.shadowColor = 'rgba(0,0,0,0.45)'; ctx.shadowBlur = width * 0.06; ctx.shadowOffsetY = width * 0.02;
        ctx.fillStyle = '#1A1A1A';
        ctx.beginPath();
        const rx = imageX - ft, ry = imageY - ft, rw = scaledWidth + ft * 2, rh = scaledHeight + ft * 2;
        ctx.moveTo(rx + br, ry); ctx.lineTo(rx + rw - br, ry); ctx.quadraticCurveTo(rx + rw, ry, rx + rw, ry + br);
        ctx.lineTo(rx + rw, ry + rh - br); ctx.quadraticCurveTo(rx + rw, ry + rh, rx + rw - br, ry + rh);
        ctx.lineTo(rx + br, ry + rh); ctx.quadraticCurveTo(rx, ry + rh, rx, ry + rh - br);
        ctx.lineTo(rx, ry + br); ctx.quadraticCurveTo(rx, ry, rx + br, ry); ctx.closePath();
        ctx.fill();
        ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(imageX + br * 0.82, imageY); ctx.lineTo(imageX + scaledWidth - br * 0.82, imageY);
        ctx.quadraticCurveTo(imageX + scaledWidth, imageY, imageX + scaledWidth, imageY + br * 0.82);
        ctx.lineTo(imageX + scaledWidth, imageY + scaledHeight - br * 0.82);
        ctx.quadraticCurveTo(imageX + scaledWidth, imageY + scaledHeight, imageX + scaledWidth - br * 0.82, imageY + scaledHeight);
        ctx.lineTo(imageX + br * 0.82, imageY + scaledHeight); ctx.quadraticCurveTo(imageX, imageY + scaledHeight, imageX, imageY + scaledHeight - br * 0.82);
        ctx.lineTo(imageX, imageY + br * 0.82); ctx.quadraticCurveTo(imageX, imageY, imageX + br * 0.82, imageY); ctx.closePath();
        ctx.clip();
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, imageX, imageY, scaledWidth, scaledHeight);
        ctx.restore();
      } else {
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, imageX, imageY, scaledWidth, scaledHeight);
      }
    };
    img.src = s.dataUrl;
  }, [s, width, height]);

  const DISPLAY_HEIGHT = 720;
  const aspectRatio = width / height;
  const displayWidth = Math.round(DISPLAY_HEIGHT * aspectRatio);

  return (
    <div style={{ width: displayWidth, height: DISPLAY_HEIGHT, margin: '0 auto' }}>
      <canvas
        ref={canvasRef}
        style={{ width: displayWidth, height: DISPLAY_HEIGHT, borderRadius: 10, display: 'block' }}
      />
    </div>
  );
}

// ── FontSelect ─────────────────────────────────────────────────────────────────
function FontSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const groups: string[] = [];
  return (
    <select
      value={value}
      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
      style={{ fontFamily: value, width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #dde', fontSize: 13, background: '#fafafa', cursor: 'pointer' }}
    >
      {FONT_FAMILIES.flatMap((f: FontFamily) => {
        const items: React.ReactNode[] = [];
        if (!groups.includes(f.group)) {
          groups.push(f.group);
          items.push(<option key={`g-${f.group}`} disabled style={{ fontWeight: 700, color: '#999', fontSize: 11 }}>── {f.group.toUpperCase()} ──</option>);
        }
        items.push(<option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>);
        return items;
      })}
    </select>
  );
}


// ── Tooltip ───────────────────────────────────────────────────────────────────
function Tip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle', marginLeft: 5 }}>
      <button
        onClick={() => setShow(v => !v)}
        onBlur={() => setTimeout(() => setShow(false), 150)}
        style={{ width: 16, height: 16, borderRadius: '50%', border: '1.5px solid #94a3b8', background: '#f1f5f9', color: '#64748b', fontSize: 10, fontWeight: 800, cursor: 'pointer', lineHeight: 1, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      >?</button>
      {show && (
        <div style={{ position: 'absolute', left: '50%', bottom: 'calc(100% + 8px)', transform: 'translateX(-50%)', background: '#1e293b', color: '#f1f5f9', borderRadius: 10, padding: '10px 14px', fontSize: 12, lineHeight: 1.6, whiteSpace: 'normal', width: 240, zIndex: 999, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', pointerEvents: 'none' }}>
          {text}
          <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1e293b' }} />
        </div>
      )}
    </span>
  );
}

// ── Walkthrough ───────────────────────────────────────────────────────────────
const TOUR_STEPS = [
  { emoji: '👋', title: 'Welcome to AppReady Studio', body: 'This quick tour shows you how to create App Store screenshots and preview videos - all inside your browser. Your files never leave your device.', section: null as string | null },
  { emoji: '📸', title: 'Upload your screenshots', body: 'Go to Screenshots and click Choose screenshots. Upload all your raw app screenshots at once - up to 10 for a full App Store slot.', section: 'screenshots' as string | null },
  { emoji: '✍️', title: 'Write or generate copy', body: 'Select a card on the left, then write your headline and body text on the right - or use the Copy assistant to generate it from a description.', section: 'screenshots' as string | null },
  { emoji: '🎨', title: 'Style your screenshots', body: 'Pick fonts, gradients, and layout options in the editing panel. Click Push ALL styles to apply your design to every screenshot at once.', section: 'screenshots' as string | null },
  { emoji: '⬇️', title: 'Export your screenshots', body: 'Click Download all to export every screenshot at full App Store resolution. Free plan exports include a watermark - upgrade to remove it.', section: 'screenshots' as string | null },
  { emoji: '🎬', title: 'Convert your preview video', body: 'Go to Preview Video, upload your screen recording, drag the trim handles to pick 15 to 30 seconds, then click Export.', section: 'video' as string | null },
  { emoji: '🔓', title: 'Remove the watermark', body: 'Already purchased? Click Sign in in the sidebar and enter the email you used at checkout. Your plan unlocks instantly.', section: 'screenshots' as string | null },
];

interface WalkthroughProps { onClose: () => void; onNavigate: (s: string) => void; }
function Walkthrough({ onClose, onNavigate }: WalkthroughProps) {
  const [step, setStep] = React.useState(0);
  const current = TOUR_STEPS[step]!;
  const isLast = step === TOUR_STEPS.length - 1;
  const finish = () => { try { localStorage.setItem('appready-tour-done','1'); } catch {} onClose(); };
  const next = () => { if (current.section) onNavigate(current.section); if (isLast) finish(); else setStep(s => s + 1); };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: '#fff', borderRadius: 24, padding: '36px 40px', maxWidth: 480, width: '100%', boxShadow: '0 32px 80px rgba(0,0,0,0.25)', position: 'relative' }}>
        <button onClick={finish} style={{ position: 'absolute', top: 16, right: 16, background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}>Skip tour</button>
        <div style={{ display: 'flex', gap: 5, marginBottom: 24 }}>
          {TOUR_STEPS.map((_: typeof TOUR_STEPS[0], i: number) => <div key={i} style={{ height: 3, flex: 1, borderRadius: 2, background: i <= step ? '#5C6BC0' : '#e2e8f0', transition: 'background 0.3s' }} />)}
        </div>
        <div style={{ fontSize: 44, marginBottom: 16, lineHeight: 1 }}>{current.emoji}</div>
        <h2 style={{ margin: '0 0 12px', fontSize: 22, color: '#1e293b', fontWeight: 800 }}>{current.title}</h2>
        <p style={{ margin: '0 0 28px', color: '#64748b', lineHeight: 1.7, fontSize: 15 }}>{current.body}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{step + 1} of {TOUR_STEPS.length}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 0 && <button onClick={() => setStep(s => s - 1)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 10, padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Back</button>}
            <button onClick={next} style={{ background: 'linear-gradient(135deg,#5C6BC0,#3949AB)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 22px', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>{isLast ? 'Start creating' : 'Next →'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ScreenshotCard ───────────────────────────────────────────────────────────
interface CardProps {
  screenshot: Screenshot;
  index: number;
  deviceSize: DeviceKey;
  onUpdate: (updates: Partial<Screenshot>) => void;
  onDelete: () => void;
  onPushToAll: (fields: (keyof Screenshot)[]) => void;
  onGenerateCopy: () => void;
}

function ScreenshotCard({ screenshot: s, index, deviceSize, onUpdate, onDelete, onPushToAll, onGenerateCopy }: CardProps) {
  const inp: React.CSSProperties = { width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box' as const, fontFamily: 'inherit', color: '#1e293b' };
  const sel: React.CSSProperties = { width: '100%', padding: '6px 9px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, background: '#fff', color: '#1e293b' };
  const lbl: React.CSSProperties = { fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5 };
  const pushBtn: React.CSSProperties = { padding: '2px 7px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#f8fafc', color: '#6b7280', fontSize: 10, cursor: 'pointer', marginLeft: 'auto' };
  const pill = (on: boolean): React.CSSProperties => ({ padding: '4px 9px', border: `1px solid ${on ? '#5C6BC0' : '#e5e7eb'}`, borderRadius: 6, background: on ? '#eef0fb' : '#fff', color: on ? '#3949AB' : '#6b7280', cursor: 'pointer', fontSize: 11, fontWeight: on ? 700 : 400 });
  const sliderRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 };
  const div: React.CSSProperties = { height: 1, background: '#f0f0f4', margin: '6px 0' };

  // Match LivePreview's display width calculation so the column is never narrower than the canvas
  const DISPLAY_HEIGHT = 720;
  const { width: devW, height: devH } = DEVICE_SIZES[deviceSize];
  const previewColWidth = Math.round(DISPLAY_HEIGHT * (devW / devH)) + 24; // +24 for column padding

  return (
    <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: '#f8fafc', borderBottom: '1px solid #f0f0f4' }}>
        <span style={{ fontWeight: 800, fontSize: 13, color: '#1e293b' }}>#{index + 1}</span>
        <span style={{ fontSize: 11, color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.file?.name}</span>
        <button onClick={onDelete} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>✕</button>
      </div>

      {/* Two column: preview left, controls right */}
      <div style={{ display: 'grid', gridTemplateColumns: `${previewColWidth}px 1fr`, minHeight: 400 }}>

        {/* LEFT - live preview */}
        <div style={{ borderRight: '1px solid #f0f0f4', padding: 12, background: '#f8fafc', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
          <div style={{ width: '100%' }}>
            <LivePreview screenshot={s} deviceSize={deviceSize} />
          </div>
        </div>

        {/* RIGHT - all controls, single scroll */}
        <div style={{ padding: '16px 16px 16px 12px', overflowY: 'auto' as const, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Copy assistant */}
          <div style={{ padding: 12, borderRadius: 10, background: '#f7f8ff', border: '1px solid #dde2fb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 12, color: '#3949AB' }}>Copy assistant</strong><button style={{ padding: '4px 10px', background: '#eef0fb', color: '#3949AB', border: '1px solid #c7ccef', borderRadius: 6, fontSize: 11, cursor: 'pointer' }} onClick={onGenerateCopy}>Generate ✨</button>
            </div>
            <select value={s.messageGoal} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate({ messageGoal: e.target.value as MessageGoal })} style={{ ...sel, marginBottom: 6 }}>
              {MESSAGE_GOALS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
            <input style={{ ...inp, marginBottom: 5 }} value={s.featureName} placeholder="What does this screen show?" onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ featureName: e.target.value })} />
            <input style={inp} value={s.benefit} placeholder="Why should users care?" onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ benefit: e.target.value })} />
          </div>

          {/* Headline */}
          <div>
            <div style={lbl}><span>Headline</span><span style={{ marginLeft: 'auto', fontSize: 10, color: s.headline.length > 50 ? '#f59e0b' : '#94a3b8', fontWeight: 400, textTransform: 'none' as const, letterSpacing: 0 }}>{s.headline.length}/60</span></div>
            <input maxLength={60} style={inp} value={s.headline} placeholder="Your headline…" onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ headline: e.target.value })} />
          </div>

          {/* Body */}
          <div>
            <div style={lbl}><span>Supporting text</span><span style={{ marginLeft: 'auto', fontSize: 10, color: s.body.length > 130 ? '#f59e0b' : '#94a3b8', fontWeight: 400, textTransform: 'none' as const, letterSpacing: 0 }}>{s.body.length}/150</span></div>
            <textarea maxLength={150} style={{ ...inp, minHeight: 60, resize: 'vertical' as const, lineHeight: '1.5' }} value={s.body} placeholder="Explain the value shown…" onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onUpdate({ body: e.target.value })} />
          </div>

          <div style={div} />

          {/* Layout */}
          <div>
            <div style={lbl}><span>Layout</span><button style={pushBtn} onClick={() => onPushToAll(['textAlign', 'textPosition', 'textShadow', 'showDeviceFrame', 'letterSpacing'])}>Push ↓</button></div>
            <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 6 }}>
              <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>Align</span>
                {(['left','center','right'] as const).map(a => <button key={a} style={pill(s.textAlign === a)} onClick={() => onUpdate({ textAlign: a })}>{a==='left'?'←':a==='center'?'≡':'→'}</button>)}
              </div>
              <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>Pos</span>
                {(['top','middle','bottom'] as const).map(p => <button key={p} style={pill(s.textPosition === p)} onClick={() => onUpdate({ textPosition: p })}>{p==='top'?'↑':p==='middle'?'↕':'↓'}</button>)}
              </div>
              <button style={pill(s.textShadow)} onClick={() => onUpdate({ textShadow: !s.textShadow })}>Shadow {s.textShadow ? 'on' : 'off'}</button>
              <button style={pill(s.showDeviceFrame)} onClick={() => onUpdate({ showDeviceFrame: !s.showDeviceFrame })}>Frame {s.showDeviceFrame ? 'on' : 'off'}</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <span style={{ fontSize: 11, color: '#94a3b8', width: 52 }}>Spacing</span>
              <input type="range" min={0} max={20} step={1} value={s.letterSpacing} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ letterSpacing: parseInt(e.target.value) })} style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: '#3949AB', fontWeight: 700, width: 30, textAlign: 'right' as const }}>{s.letterSpacing}px</span>
            </div>
          </div>

          <div style={div} />

          {/* Headline font */}
          <div>
            <div style={lbl}><span>Headline font</span><button style={pushBtn} onClick={() => onPushToAll(['headlineFontFamily','headlineFontWeight','headlineFontSize','headlineFontItalic'])}>Push ↓</button></div>
            <FontSelect value={s.headlineFontFamily} onChange={(v: string) => onUpdate({ headlineFontFamily: v })} />
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <select value={s.headlineFontWeight} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate({ headlineFontWeight: e.target.value })} style={{ ...sel, flex: 1 }}>
                {FONT_WEIGHTS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
              <button style={pill(s.headlineFontItalic)} onClick={() => onUpdate({ headlineFontItalic: !s.headlineFontItalic })}>Italic</button>
            </div>
            <div style={sliderRow}>
              <span style={{ fontSize: 11, color: '#94a3b8', width: 36 }}>Size</span>
              <input type="range" min={0.5} max={3.0} step={0.05} value={s.headlineFontSize} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ headlineFontSize: parseFloat(e.target.value) })} style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: '#3949AB', fontWeight: 700, width: 36, textAlign: 'right' as const }}>{Math.round(s.headlineFontSize * 100)}%</span>
            </div>
          </div>

          {/* Body font */}
          <div>
            <div style={lbl}><span>Body font</span><button style={pushBtn} onClick={() => onPushToAll(['bodyFontFamily','bodyFontWeight','bodyFontSize'])}>Push ↓</button></div>
            <FontSelect value={s.bodyFontFamily} onChange={(v: string) => onUpdate({ bodyFontFamily: v })} />
            <div style={{ marginTop: 6 }}>
              <select value={s.bodyFontWeight} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdate({ bodyFontWeight: e.target.value })} style={sel}>
                {FONT_WEIGHTS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
            </div>
            <div style={sliderRow}>
              <span style={{ fontSize: 11, color: '#94a3b8', width: 36 }}>Size</span>
              <input type="range" min={0.5} max={2.0} step={0.05} value={s.bodyFontSize} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ bodyFontSize: parseFloat(e.target.value) })} style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: '#3949AB', fontWeight: 700, width: 36, textAlign: 'right' as const }}>{Math.round(s.bodyFontSize * 100)}%</span>
            </div>
          </div>

          <div style={div} />

          {/* Gradient presets */}
          <div>
            <div style={lbl}><span>Gradient presets</span><button style={pushBtn} onClick={() => onPushToAll(['gradientStart','gradientEnd','textColor'])}>Push ↓</button></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8,1fr)', gap: 5 }}>
              {GRADIENT_PRESETS.map((p: GradientPreset) => (
                <button key={p.name} title={p.name} style={{ aspectRatio: '1', borderRadius: 7, border: '2px solid rgba(255,255,255,0.5)', cursor: 'pointer', background: `linear-gradient(135deg,${p.start},${p.end})` }} onClick={() => onUpdate({ gradientStart: p.start, gradientEnd: p.end })} />
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
              {([['Start','gradientStart'],['End','gradientEnd'],['Text','textColor']] as [string, keyof Screenshot][]).map(([label, key]) => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <input type="color" value={s[key] as string} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ [key]: e.target.value })} style={{ width: '100%', height: 34, borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer' }} />
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Push all + delete */}
          <div style={{ display: 'flex', gap: 8, marginTop: 4, paddingTop: 8, borderTop: '1px solid #f0f0f4' }}>
            <button style={{ flex: 1, padding: '9px', background: 'linear-gradient(135deg,#43A047,#2E7D32)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              onClick={() => onPushToAll(['headlineFontFamily','headlineFontWeight','headlineFontSize','headlineFontItalic','bodyFontFamily','bodyFontWeight','bodyFontSize','textAlign','textPosition','textShadow','letterSpacing','gradientStart','gradientEnd','textColor','showDeviceFrame'])}>
              🚀 Push ALL styles to all
            </button>
            <button style={{ padding: '9px 12px', background: '#fff0f0', color: '#e53935', border: '1px solid #ffcdd2', borderRadius: 8, fontSize: 13, cursor: 'pointer' }} onClick={onDelete}>🗑️</button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── App ────────────────────────────────────────────────────────────────────────
function App() {
  const [section, setSection] = useState<AppSection>('dashboard');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [showTour, setShowTour] = useState<boolean>(() => {
    try { return localStorage.getItem('appready-tour-done') !== '1'; }
    catch { return true; }
  });

  const [projectId, setProjectId] = useState<string>(() => `project-${Date.now()}`);
  const [projectName, setProjectName] = useState<string>('My App Launch');
  const [appAudience, setAppAudience] = useState<string>('');
  const [corePromise, setCorePromise] = useState<string>('');
  const [copyTone, setCopyTone] = useState<CopyTone>('clear');
  const [globalNotice, setGlobalNotice] = useState<string>('');

  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [deviceSize, setDeviceSize] = useState<DeviceKey>('iphone-6.9');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpgQuality, setJpgQuality] = useState<number>(95);
  const [processing, setProcessing] = useState<boolean>(false);
  const [zipExporting, setZipExporting] = useState<boolean>(false);
  const [showPaywall, setShowPaywall] = useState<boolean>(false);
  const [paywallReason, setPaywallReason] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>(() => localStorage.getItem('appready-email') || '');

  // Dev bypass: set VITE_DEV_BYPASS=true locally to skip watermarks and paywalls.
  // This env var is never set in Cloudflare Pages, so it has no effect in production.
const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS === 'true';   

  const [userPlan, setUserPlan] = useState<string>(() => DEV_BYPASS ? 'indie-pro' : (localStorage.getItem('appready-plan') || ''));
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [authEmail, setAuthEmail] = useState<string>('');
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string>('');
 

  const [videoPresetKey, setVideoPresetKey] = useState<string>('iphone-modern-portrait');
  const [videoFit, setVideoFit] = useState<VideoFitMode>('contain');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [videoInfo, setVideoInfo] = useState<VideoInfo | null>(null);
  const [trimStart, setTrimStart] = useState<number>(0);
  const [trimEnd, setTrimEnd] = useState<number>(30);
  const [posterTime, setPosterTime] = useState<number>(5);
  const [videoProcessing, setVideoProcessing] = useState<boolean>(false);
  const [videoProgress, setVideoProgress] = useState<number>(0);
  const [videoStatus, setVideoStatus] = useState<string>('');
  const [lastExport, setLastExport] = useState<ExportResult | null>(null);
  const [confirmRealFootage, setConfirmRealFootage] = useState<boolean>(false);
  const [confirmClaims, setConfirmClaims] = useState<boolean>(false);

  const [savedProjects, setSavedProjects] = useState<SavedProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState<boolean>(true);

  const ffmpegRef = useRef<FFmpegWorkerClient | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoPreviewUrlRef = useRef<string | null>(null);
  const lastExportUrlRef = useRef<string | null>(null);
  const ffmpegLogRef = useRef<string[]>([]);

  const selectedVideoPreset = VIDEO_PRESETS.find(p => p.key === videoPresetKey) || VIDEO_PRESETS[0]!;
  const selectedScreenshotSize = DEVICE_SIZES[deviceSize];
  const trimDuration = Math.max(0, trimEnd - trimStart);

  useEffect(() => {
    if (!document.getElementById('gfonts-inject')) {
      const link = document.createElement('link');
      link.id = 'gfonts-inject';
      link.rel = 'stylesheet';
      link.href = GOOGLE_FONTS_URL;
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    getSavedProjects()
      .then(setSavedProjects)
      .catch(error => setGlobalNotice(`Could not load saved projects: ${String(error)}`))
      .finally(() => setProjectsLoading(false));
  }, []);

  useEffect(() => {
    videoPreviewUrlRef.current = videoPreviewUrl;
  }, [videoPreviewUrl]);

  useEffect(() => {
    lastExportUrlRef.current = lastExport?.url || null;
  }, [lastExport]);

  useEffect(() => {
    return () => {
      ffmpegRef.current?.worker.terminate();
      ffmpegRef.current = null;
      if (videoPreviewUrlRef.current) URL.revokeObjectURL(videoPreviewUrlRef.current);
      if (lastExportUrlRef.current) URL.revokeObjectURL(lastExportUrlRef.current);
    };
  }, []);

  const refreshProjects = async () => {
    setSavedProjects(await getSavedProjects());
  };

  const handleFilesUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    let processed = 0;
    const newScreenshots: Screenshot[] = [];
    files.forEach((file: File, index: number) => {
      const reader = new FileReader();
      reader.onload = (ev: ProgressEvent<FileReader>) => {
        const id = `ss-${Date.now()}-${index}`;
        const gradient = GRADIENT_PRESETS[(screenshots.length + index) % GRADIENT_PRESETS.length]!;
        newScreenshots.push({
          id,
          file,
          dataUrl: ev.target?.result as string,
          headline: '',
          body: '',
          featureName: humanizeFileName(file.name),
          benefit: '',
          messageGoal: index === 0 ? 'benefit' : 'feature',
          gradientStart: gradient.start,
          gradientEnd: gradient.end,
          textColor: '#FFFFFF',
          ...DEFAULT_TYPOGRAPHY,
        });
        processed += 1;
        if (processed === files.length) {
          newScreenshots.sort((a, b) => a.file.name.localeCompare(b.file.name));
          setScreenshots(prev => [...prev, ...newScreenshots]);
        }
      };
      reader.onerror = () => setGlobalNotice(`Could not read ${file.name}`);
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const updateScreenshot = (id: string, updates: Partial<Screenshot>) => {
    setScreenshots(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const generateScreenshotCopy = (id: string) => {
    setScreenshots(prev => prev.map(screenshot => {
      if (screenshot.id !== id) return screenshot;
      return { ...screenshot, ...createScreenshotCopy(screenshot, projectName, appAudience, corePromise, copyTone) };
    }));
  };

  const generateAllScreenshotCopy = () => {
    if (!screenshots.length) return;
    if (!corePromise.trim() && screenshots.every(screenshot => !screenshot.benefit.trim())) {
      setGlobalNotice('Add your app’s main promise, or enter a benefit on each screenshot, before generating copy.');
      return;
    }
    setScreenshots(prev => prev.map(screenshot => ({
      ...screenshot,
      ...createScreenshotCopy(screenshot, projectName, appAudience, corePromise, copyTone),
    })));
    setGlobalNotice('Generated editable copy for every screenshot. Review each line before exporting.');
  };

  const deleteScreenshot = (id: string) => {
    setScreenshots(prev => prev.filter(s => s.id !== id));
  };

  const pushToAll = (sourceId: string, fields: (keyof Screenshot)[]) => {
    const source = screenshots.find(s => s.id === sourceId);
    if (!source) return;
    const updates: Partial<Screenshot> = {};
    fields.forEach(field => {
      (updates as Record<keyof Screenshot, unknown>)[field] = source[field];
    });
    setScreenshots(prev => prev.map(s => s.id === sourceId ? s : { ...s, ...updates }));
  };

  const drawScreenshot = useCallback((screenshot: Screenshot): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const canvas = canvasRef.current;
      if (!canvas) { reject(new Error('Canvas is unavailable')); return; }
      const { width, height } = DEVICE_SIZES[deviceSize];
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas context is unavailable')); return; }

      const img = new Image();
      img.onload = () => {
        const grad = ctx.createLinearGradient(0, 0, 0, height);
        const c1 = hexToRgb(screenshot.gradientStart);
        const c2 = hexToRgb(screenshot.gradientEnd);
        grad.addColorStop(0, `rgb(${Math.round(c1.r * 255)},${Math.round(c1.g * 255)},${Math.round(c1.b * 255)})`);
        grad.addColorStop(1, `rgb(${Math.round(c2.r * 255)},${Math.round(c2.g * 255)},${Math.round(c2.b * 255)})`);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);

        const textZone = height * 0.22;
        const imageTopPadding = 30;
        const maxImageHeight = height * 0.73;
        const maxImageWidth = width * 0.88;
        const frameScale = screenshot.showDeviceFrame ? 0.92 : 1;
        const sourceWidth = img.naturalWidth || img.width;
        const sourceHeight = img.naturalHeight || img.height;
        const scale = Math.min(maxImageWidth * frameScale / sourceWidth, maxImageHeight * frameScale / sourceHeight);
        const scaledWidth = sourceWidth * scale;
        const scaledHeight = sourceHeight * scale;

        let textY = textZone * 0.18;
        let imageY = textZone + imageTopPadding;
        if (screenshot.textPosition === 'bottom') {
          imageY = imageTopPadding;
          textY = imageY + scaledHeight + 40;
        } else if (screenshot.textPosition === 'middle') {
          imageY = textZone + (height - textZone - scaledHeight) / 2;
        }

        ctx.textAlign = screenshot.textAlign as CanvasTextAlign;
        const textX = screenshot.textAlign === 'left' ? width * 0.07 : screenshot.textAlign === 'right' ? width * 0.93 : width / 2;
        const maxTextWidth = width * 0.86;

        const applyShadow = (enabled: boolean) => {
          ctx.shadowColor = enabled ? 'rgba(0,0,0,0.5)' : 'transparent';
          ctx.shadowBlur = enabled ? 12 : 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = enabled ? 3 : 0;
        };

        const drawTracked = (text: string, x: number, y: number, spacing: number) => {
          if (!spacing) { ctx.fillText(text, x, y); return; }
          const chars = text.split('');
          let currentX = x;
          if (screenshot.textAlign === 'center') currentX = x - ctx.measureText(text).width / 2 - spacing * (chars.length - 1) / 2;
          if (screenshot.textAlign === 'right') currentX = x - ctx.measureText(text).width - spacing * (chars.length - 1);
          const savedAlign = ctx.textAlign;
          ctx.textAlign = 'left';
          chars.forEach(character => {
            ctx.fillText(character, currentX, y);
            currentX += ctx.measureText(character).width + spacing;
          });
          ctx.textAlign = savedAlign;
        };

        const headlinePx = Math.round((width / 18) * screenshot.headlineFontSize);
        ctx.font = `${screenshot.headlineFontItalic ? 'italic ' : ''}${screenshot.headlineFontWeight} ${headlinePx}px ${screenshot.headlineFontFamily}`;
        ctx.fillStyle = screenshot.textColor;
        ctx.textBaseline = 'top';
        applyShadow(screenshot.textShadow);
        const headlineLines = wrapText(ctx, screenshot.headline, maxTextWidth);
        const headlineLineHeight = headlinePx * 1.25;
        headlineLines.forEach((line, index) => drawTracked(line, textX, textY + index * headlineLineHeight, screenshot.letterSpacing));

        if (screenshot.body) {
          const bodyPx = Math.round((width / 36) * screenshot.bodyFontSize);
          ctx.font = `${screenshot.bodyFontWeight} ${bodyPx}px ${screenshot.bodyFontFamily}`;
          ctx.globalAlpha = 0.88;
          const bodyY = textY + headlineLines.length * headlineLineHeight + headlinePx * 0.55;
          const bodyLines = wrapText(ctx, screenshot.body, maxTextWidth);
          bodyLines.forEach((line, index) => drawTracked(line, textX, bodyY + index * bodyPx * 1.5, screenshot.letterSpacing * 0.5));
          ctx.globalAlpha = 1;
        }
        applyShadow(false);

        const imageX = (width - scaledWidth) / 2;
        if (screenshot.showDeviceFrame) {
          const frameThickness = width * 0.025;
          const borderRadius = width * 0.1;
          const frameX = imageX - frameThickness;
          const frameY = imageY - frameThickness;
          const frameWidth = scaledWidth + frameThickness * 2;
          const frameHeight = scaledHeight + frameThickness * 2;
          ctx.shadowColor = 'rgba(0,0,0,0.45)';
          ctx.shadowBlur = width * 0.06;
          ctx.shadowOffsetY = width * 0.02;
          ctx.fillStyle = '#1A1A1A';
          ctx.beginPath();
          roundRect(ctx, frameX, frameY, frameWidth, frameHeight, borderRadius);
          ctx.fill();
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;
          ctx.save();
          ctx.beginPath();
          roundRect(ctx, imageX, imageY, scaledWidth, scaledHeight, borderRadius * 0.82);
          ctx.clip();
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, imageX, imageY, scaledWidth, scaledHeight);
          ctx.restore();
        } else {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, imageX, imageY, scaledWidth, scaledHeight);
        }

        // Watermark for free users - centered, hard to crop
        if (!userPlan && !DEV_BYPASS) {
          const wh = Math.round(width * 0.032);
          ctx.save();
          // Semi-transparent dark overlay across center third
          ctx.globalAlpha = 0.38;
          ctx.fillStyle = 'rgba(0,0,0,0.6)';
          ctx.fillRect(0, height * 0.38, width, height * 0.24);
          // Diagonal tiled watermark text
          ctx.globalAlpha = 0.55;
          ctx.font = `700 ${wh}px -apple-system, BlinkMacSystemFont, sans-serif`;
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          // Rotate and tile
          const text = 'AppReady Studio - Free';
          const textW = ctx.measureText(text).width;
          ctx.translate(width / 2, height / 2);
          ctx.rotate(-Math.PI / 6);
          for (let row = -3; row <= 3; row++) {
            for (let col = -2; col <= 2; col++) {
              ctx.fillText(text, col * (textW + 40), row * (wh * 2.8));
            }
          }
          ctx.restore();
        }
        const mime = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
        const quality = outputFormat === 'jpg' ? jpgQuality / 100 : undefined;
        canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Could not create image')), mime, quality);
      };
      img.onerror = () => reject(new Error('Could not load screenshot image'));
      img.src = screenshot.dataUrl;
    });
  }, [deviceSize, outputFormat, jpgQuality, userPlan, DEV_BYPASS]);

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const handleBatchDownload = async () => {
    if (!screenshots.length) return;
    setProcessing(true);
    setGlobalNotice('');
    try {
      for (let index = 0; index < screenshots.length; index += 1) {
        const screenshot = screenshots[index]!;
        const blob = await drawScreenshot(screenshot);
        const cleanName = screenshot.headline.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `screenshot-${index + 1}`;
        downloadBlob(blob, `${String(index + 1).padStart(2, '0')}-${cleanName}-${selectedScreenshotSize.width}x${selectedScreenshotSize.height}.${outputFormat}`);
        await new Promise<void>(resolve => window.setTimeout(resolve, 350));
      }
      setGlobalNotice(!userPlan && !DEV_BYPASS ? `Exported ${screenshots.length} draft screenshots with the free-plan watermark. Do not submit these to App Store Connect.` : `Exported ${screenshots.length} App Store screenshots.`);
    } catch (error) {
      setGlobalNotice(`Screenshot export failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleZipDownload = async () => {
    if (!screenshots.length) return;
    if (userPlan !== 'indie-pro') {
      setPaywallReason('zip');
      setShowPaywall(true);
      return;
    }
    setZipExporting(true);
    setGlobalNotice('');
    try {
      if (typeof (window as any).JSZip === 'undefined') {
        await new Promise<void>((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
          s.onload = () => resolve();
          s.onerror = () => reject(new Error('Could not load JSZip'));
          document.head.appendChild(s);
        });
      }
      const JSZipLib = (window as any).JSZip;
      const zip = new JSZipLib();
      const folderName = (projectName || 'app-screenshots').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const folder = zip.folder(folderName);
      for (let i = 0; i < screenshots.length; i++) {
        const screenshot = screenshots[i]!;
        const blob = await drawScreenshot(screenshot);
        const cleanName = screenshot.headline.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `screenshot-${i + 1}`;
        const fileName = String(i + 1).padStart(2, '0') + '-' + cleanName + '-' + selectedScreenshotSize.width + 'x' + selectedScreenshotSize.height + '.' + outputFormat;
        folder.file(fileName, blob);
      }
      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      downloadBlob(zipBlob, folderName + '-screenshots.zip');
      setGlobalNotice('Zipped ' + screenshots.length + ' screenshots into one download.');
    } catch (error) {
      setGlobalNotice('ZIP export failed: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setZipExporting(false);
    }
  };

  const publicAsset = (path: string): string => `${window.location.origin}/${path.replace(/^\/+/, '')}`;

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    if (lastExport) {
      URL.revokeObjectURL(lastExport.url);
      setLastExport(null);
    }

    const url = URL.createObjectURL(file);
    setVideoFile(file);
    setVideoPreviewUrl(url);
    setVideoInfo(null);
    setVideoStatus('Reading video metadata…');

    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    tempVideo.onloadedmetadata = () => {
      const duration = Number.isFinite(tempVideo.duration) ? tempVideo.duration : 0;
      setVideoInfo({ duration, w: tempVideo.videoWidth, h: tempVideo.videoHeight });
      setTrimStart(0);
      setTrimEnd(Math.min(30, duration));
      setPosterTime(Math.min(5, Math.max(0, duration - 0.1)));
      setVideoStatus('');
      tempVideo.removeAttribute('src');
      tempVideo.load();
    };
    tempVideo.onerror = () => setVideoStatus('❌ Could not read this video file. Try an MP4, MOV or M4V recording.');
    tempVideo.src = url;
    e.target.value = '';
  };

  const loadFFmpeg = async (): Promise<FFmpegWorkerClient> => {
    if (ffmpegRef.current) return ffmpegRef.current;

    setVideoStatus('Loading FFmpeg worker…');
    const worker = new Worker(publicAsset('/ffmpeg/ffmpeg-worker.js'));
    let nextRequestId = 1;
    const pending = new Map<number, { resolve: (value: unknown) => void; reject: (reason?: unknown) => void }>();

    const rejectAll = (error: Error) => {
      pending.forEach(({ reject }) => reject(error));
      pending.clear();
    };

    const request = (type: string, payload: Record<string, unknown> = {}, transfer: Transferable[] = []): Promise<unknown> => {
      const id = nextRequestId++;
      return new Promise((resolve, reject) => {
        pending.set(id, { resolve, reject });
        worker.postMessage({ id, type, ...payload }, transfer);
      });
    };

    worker.onmessage = (event: MessageEvent<FFmpegWorkerMessage>) => {
      const message = event.data;
      if (message.type === 'progress') {
        const progress = Number(message.progress ?? 0);
        if (Number.isFinite(progress)) setVideoProgress(Math.max(0, Math.min(100, Math.round(progress * 100))));
        return;
      }
      if (message.type === 'log') {
        if (message.message) {
          console.debug('[FFmpeg]', message.message);
          ffmpegLogRef.current.push(message.message);
          if (ffmpegLogRef.current.length > 60) ffmpegLogRef.current.shift();
        }
        return;
      }
      if (typeof message.id !== 'number') return;
      const callback = pending.get(message.id);
      if (!callback) return;
      pending.delete(message.id);
      if (message.type === 'error') callback.reject(new Error(message.message || 'FFmpeg worker error'));
      else callback.resolve(message.data);
    };

    worker.onerror = event => rejectAll(new Error(event.message || 'Could not start FFmpeg worker'));
    const client: FFmpegWorkerClient = { worker, request };

    try {
      setVideoStatus('Loading FFmpeg core. The first load is about 31 MB and is cached afterward…');
     await request('load', {
  coreURL: publicAsset('/ffmpeg/ffmpeg-core.js'),
  wasmURL: 'https://ffmpeg-wasm-proxy.mtobias2000.workers.dev',
});
      ffmpegRef.current = client;
      return client;
    } catch (error) {
      worker.terminate();
      rejectAll(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  };

  const isFreeWatermarked = !userPlan && !DEV_BYPASS;
  const aspectDiff = videoInfo ? Math.abs(videoInfo.w / videoInfo.h - selectedVideoPreset.width / selectedVideoPreset.height) / (selectedVideoPreset.width / selectedVideoPreset.height) : 0;
  const needsPadding = !!videoInfo && aspectDiff > 0.03;
  const RISKY_CLAIMS = /(#\s?1|number\s?one|\bbest\b|\bfree\b|\bfastest\b|\bfaster\b|\bAI\b|\bguarantee|\bno\.\s?1|\$\d|\d+%|award|\bnew\b|\bsale\b|\bdiscount)/i;
  const riskyCopyCount = screenshots.filter(sc => RISKY_CLAIMS.test(`${sc.headline} ${sc.body}`)).length;

  const videoValidation: ValidationItem[] = [
    {
      label: 'Watermark (App Store safe)',
      status: !videoFile ? 'info' : isFreeWatermarked ? 'fail' : 'pass',
      detail: isFreeWatermarked
        ? 'Free-plan exports burn an AppReady Studio banner over your app footage. Apple requires previews to show only the app itself (Guideline 2.3.4). Use this export for drafts only, never for submission.'
        : 'No watermark is added to this export.',
    },
    {
      label: 'No framing or black bars',
      status: !videoInfo ? 'info' : !needsPadding ? 'pass' : videoFit === 'contain' ? 'fail' : 'warn',
      detail: !videoInfo
        ? 'Choose a source recording to check it against the target size.'
        : !needsPadding
          ? `Source ${videoInfo.w} × ${videoInfo.h} matches the ${selectedVideoPreset.width} × ${selectedVideoPreset.height} target, so nothing is padded or cropped.`
          : videoFit === 'contain'
            ? `Source is ${videoInfo.w} × ${videoInfo.h} but the target is ${selectedVideoPreset.width} × ${selectedVideoPreset.height}. Contain adds black bars around your recording, which Apple can reject as framing (2.3.4). Re-record on the device or simulator at the target size, or pick the matching target.`
            : `Source is ${videoInfo.w} × ${videoInfo.h} but the target is ${selectedVideoPreset.width} × ${selectedVideoPreset.height}. Cover crops the edges, so check that no UI is cut off.`,
    },
    {
      label: 'Footage is real app capture',
      status: confirmRealFootage ? 'pass' : 'warn',
      detail: confirmRealFootage
        ? 'You confirmed the recording is a full-screen capture of the app with no device frame, bezel or background around it.'
        : 'Confirm below that the recording is a full-screen capture of the app. It must not include a device frame or bezel, simulator window chrome, a backdrop, or footage of other apps.',
    },
    {
      label: 'Video selected',
      status: videoFile ? 'pass' : 'fail',
      detail: videoFile ? videoFile.name : 'Choose a source screen recording.',
    },
    {
      label: 'Duration',
      status: !videoFile ? 'info' : trimDuration >= 15 && trimDuration <= 30 ? 'pass' : 'fail',
      detail: `${trimDuration.toFixed(1)} seconds selected. Apple requires 15 to 30 seconds.`,
    },
    {
      label: 'File size',
      status: !videoFile ? 'info' : videoFile.size <= 500 * 1024 * 1024 ? 'pass' : 'fail',
      detail: videoFile ? `${(videoFile.size / 1024 / 1024).toFixed(1)} MB source. Apple allows up to 500 MB.` : 'Apple allows up to 500 MB.',
    },
    {
      label: 'Resolution',
      status: 'pass',
      detail: `${selectedVideoPreset.width} × ${selectedVideoPreset.height} (${selectedVideoPreset.label}).`,
    },
    {
      label: 'Video codec and rate',
      status: 'pass',
      detail: 'H.264 High Profile Level 4.0, progressive, 30 fps, target 10 to 12 Mbps.',
    },
    {
      label: 'Audio',
      status: 'pass',
      detail: 'Enabled AAC-LC, 48 kHz, 2-channel stereo, 256 kbps target.',
    },
    {
      label: 'Privacy',
      status: 'pass',
      detail: 'Encoding happens locally in this browser. The recording is not uploaded to this app.',
    },
  ];

  const screenshotValidation: ValidationItem[] = [
    {
      label: 'Watermark (App Store safe)',
      status: screenshots.length === 0 ? 'info' : isFreeWatermarked ? 'fail' : 'pass',
      detail: isFreeWatermarked
        ? 'Free-plan screenshots carry a dark band and tiled AppReady Studio text across the middle. Do not submit these to App Store Connect.'
        : 'No watermark is added to these exports.',
    },
    {
      label: 'Claims in copy',
      status: screenshots.length === 0 ? 'info' : riskyCopyCount ? 'warn' : 'pass',
      detail: riskyCopyCount
        ? `${riskyCopyCount} screenshot${riskyCopyCount === 1 ? '' : 's'} use superlatives, prices, percentages or words like "free", "AI" or "faster". Apple (2.3.1, 2.3.4, 2.3.7) can reject text that is not true in the app today.`
        : 'No superlatives, price or percentage claims detected. Still read every line against your app.',
    },
    {
      label: 'Screens show the shipping app',
      status: confirmClaims ? 'pass' : 'warn',
      detail: confirmClaims
        ? 'You confirmed every screenshot is the current build and every headline describes a feature that exists.'
        : 'Confirm below that each screenshot is from the current build and every headline describes a feature that exists in it.',
    },
    {
      label: 'Screenshot count',
      status: screenshots.length >= 1 && screenshots.length <= 10 ? 'pass' : screenshots.length === 0 ? 'fail' : 'warn',
      detail: `${screenshots.length} screenshot${screenshots.length === 1 ? '' : 's'} loaded. Apple accepts 1 to 10 per device size.`,
    },
    {
      label: 'Export dimensions',
      status: 'pass',
      detail: `${selectedScreenshotSize.width} × ${selectedScreenshotSize.height} for ${selectedScreenshotSize.name}.`,
    },
    {
      label: 'File format',
      status: 'pass',
      detail: `${outputFormat.toUpperCase()} export. The canvas background is opaque, so no alpha channel is included.`,
    },
    {
      label: 'Marketing copy',
      status: screenshots.length === 0 ? 'info' : screenshots.every(screenshot => screenshot.headline.trim() && screenshot.body.trim()) ? 'pass' : 'warn',
      detail: screenshots.length === 0
        ? 'Upload screenshots, then add or generate a unique headline and supporting line for each one.'
        : `${screenshots.filter(screenshot => screenshot.headline.trim() && screenshot.body.trim()).length} of ${screenshots.length} screenshots have complete copy.`,
    },
  ];

  const allChecks = [...videoValidation, ...screenshotValidation];
  const failedChecks = allChecks.filter(check => check.status === 'fail').length;
  const passedChecks = allChecks.filter(check => check.status === 'pass').length;
  const readinessScore = Math.round((passedChecks / allChecks.length) * 100);

  const handleConvertVideo = async () => {
    const file = videoFile;
    if (!file || !videoInfo) return;
    if (trimDuration < 15 || trimDuration > 30) {
      setVideoStatus('❌ Choose a clip between 15 and 30 seconds before exporting.');
      return;
    }

    if (videoFit === 'contain' && videoInfo.w && videoInfo.h) {
      const srcRatio = videoInfo.w / videoInfo.h;
      const dstRatio = selectedVideoPreset.width / selectedVideoPreset.height;
      if (Math.abs(srcRatio - dstRatio) / dstRatio > 0.03 &&
        !window.confirm('This recording does not match the target size, so black bars will be added around it. Apple can reject that as framing around the app capture (Guideline 2.3.4). Export anyway?')) {
        return;
      }
    }

    setVideoProcessing(true);
    setVideoProgress(0);
    setVideoStatus('');

    const extensionMatch = file.name.match(/\.[a-z0-9]+$/i);
    const inputName = `input${extensionMatch?.[0]?.toLowerCase() || '.mp4'}`;
    const outputName = 'output.mp4';
    const watermarkName = 'wm.png';

    try {
      const { request } = await loadFFmpeg();
      const targetWidth = selectedVideoPreset.width;
      const targetHeight = selectedVideoPreset.height;
      const baseScale = videoFit === 'contain'
        ? `scale='if(gt(iw/ih,${targetWidth}/${targetHeight}),${targetWidth},-2)':'if(gt(iw/ih,${targetWidth}/${targetHeight}),-2,${targetHeight})',pad=${targetWidth}:${targetHeight}:(ow-iw)/2:(oh-ih)/2:black,setsar=1`
        : `scale='if(gt(iw/ih,${targetWidth}/${targetHeight}),-2,${targetWidth})':'if(gt(iw/ih,${targetWidth}/${targetHeight}),${targetHeight},-2)',crop=${targetWidth}:${targetHeight},setsar=1`;

      setVideoStatus('Reading input file…');
      const inputBytes = new Uint8Array(await file.arrayBuffer());
      await request('writeFile', { path: inputName, data: inputBytes }, [inputBytes.buffer]);

      // Free plan: burn the watermark in with `overlay` (no fonts needed).
      // Paid plan / dev bypass: plain scale/pad/crop filter.
      const inputArgs: string[] = ['-ss', trimStart.toFixed(3), '-i', inputName];
      let filterArgs: string[];
      let audioInputIndex = 1;
      if (!userPlan) {
        const watermark = await makeWatermarkPng(targetWidth, targetHeight);
        await request('writeFile', { path: watermarkName, data: watermark }, [watermark.buffer]);
        inputArgs.push('-i', watermarkName);
        audioInputIndex = 2;
        filterArgs = [
          '-filter_complex', `[0:v]${baseScale}[bg];[bg][1:v]overlay=0:0,format=yuv420p[vout]`,
          '-map', '[vout]',
        ];
      } else {
        filterArgs = ['-vf', baseScale, '-map', '0:v:0'];
      }

      setVideoStatus('Encoding App Store-ready video. Keep this tab open…');
      ffmpegLogRef.current = [];
      const exitCode = Number(await request('exec', {
        args: [
          ...inputArgs,
          '-f', 'lavfi',
          '-i', 'anoisesrc=color=white:amplitude=0.00001:sample_rate=48000',
          '-t', trimDuration.toFixed(3),
          ...filterArgs,
          '-map', `${audioInputIndex}:a:0`,
          '-c:v', 'libx264',
          '-preset', 'fast',
          '-profile:v', 'high',
          '-level:v', '4.0',
          '-r', '30',
          '-b:v', '11M',
          '-minrate', '10M',
          '-maxrate', '12M',
          '-bufsize', '24M',
          '-x264-params', 'nal-hrd=cbr:force-cfr=1',
          '-pix_fmt', 'yuv420p',
          '-c:a', 'aac',
          '-profile:a', 'aac_low',
          '-b:a', '256k',
          '-ar', '48000',
          '-ac', '2',
          '-disposition:a:0', 'default',
          '-shortest',
          '-movflags', '+faststart',
          outputName,
        ],
      }));

      if (exitCode !== 0) {
        const tail = ffmpegLogRef.current.slice(-6).join(' | ');
        throw new Error(`FFmpeg exited with code ${exitCode}${tail ? `: ${tail}` : ''}`);
      }

      setVideoStatus('Packaging your download…');
      const result = await request('readFile', { path: outputName });
      const bytes = result instanceof Uint8Array ? result : new Uint8Array(result as ArrayBuffer);
      const outputBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
      const blob = new Blob([outputBuffer], { type: 'video/mp4' });
      if (lastExport) URL.revokeObjectURL(lastExport.url);
      const url = URL.createObjectURL(blob);
      const safeProjectName = projectName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'app-preview';
      const fileName = `${safeProjectName}-${targetWidth}x${targetHeight}.mp4`;
      const exportResult: ExportResult = {
        url,
        blob,
        fileName,
        width: targetWidth,
        height: targetHeight,
        duration: trimDuration,
        createdAt: Date.now(),
      };
      setLastExport(exportResult);
      downloadBlob(blob, fileName);
      setVideoStatus(!userPlan && !DEV_BYPASS ? '⚠️ Draft exported with the free-plan watermark. Do NOT submit this to App Store Connect. Upgrade or sign in, then export again.' : '✅ Export complete. The file is ready for App Store Connect.');
    } catch (error) {
      console.error(error);
      setVideoStatus(`❌ ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      const client = ffmpegRef.current;
      if (client) {
        await client.request('deleteFile', { path: inputName }).catch(() => undefined);
        await client.request('deleteFile', { path: outputName }).catch(() => undefined);
        await client.request('deleteFile', { path: watermarkName }).catch(() => undefined);
      }
      setVideoProcessing(false);
      setVideoProgress(0);
    }
  };

  const capturePosterFrame = async () => {
    if (!userPlan) {
      setPaywallReason('poster');
      setShowPaywall(true);
      return;
    }
    if (!videoPreviewUrl || !videoInfo) {
      setGlobalNotice('Choose a video before exporting a poster frame.');
      return;
    }
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.src = videoPreviewUrl;

    try {
      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error('Could not load video for poster frame'));
      });
      video.currentTime = Math.min(Math.max(0, posterTime), Math.max(0, video.duration - 0.05));
      await new Promise<void>((resolve, reject) => {
        video.onseeked = () => resolve();
        video.onerror = () => reject(new Error('Could not seek video'));
      });

      const canvas = document.createElement('canvas');
      canvas.width = selectedVideoPreset.width;
      canvas.height = selectedVideoPreset.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas is unavailable');
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const sourceRatio = video.videoWidth / video.videoHeight;
      const targetRatio = canvas.width / canvas.height;
      let drawWidth = canvas.width;
      let drawHeight = canvas.height;
      let drawX = 0;
      let drawY = 0;
      if (videoFit === 'contain') {
        if (sourceRatio > targetRatio) {
          drawHeight = canvas.width / sourceRatio;
          drawY = (canvas.height - drawHeight) / 2;
        } else {
          drawWidth = canvas.height * sourceRatio;
          drawX = (canvas.width - drawWidth) / 2;
        }
      } else if (sourceRatio > targetRatio) {
        drawWidth = canvas.height * sourceRatio;
        drawX = (canvas.width - drawWidth) / 2;
      } else {
        drawHeight = canvas.width / sourceRatio;
        drawY = (canvas.height - drawHeight) / 2;
      }
      ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
      const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('Could not create poster image')), 'image/png'));
      downloadBlob(blob, `${projectName.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'app'}-poster-${selectedVideoPreset.width}x${selectedVideoPreset.height}.png`);
      setGlobalNotice('Poster frame exported. Apple defaults the poster frame to 5 seconds, and you can change it in App Store Connect.');
    } catch (error) {
      setGlobalNotice(`Poster frame failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      video.removeAttribute('src');
      video.load();
    }
  };

  const downloadValidationReport = () => {
    const lines = [
      'APPREADY STUDIO - APP STORE ASSET PREFLIGHT REPORT',
      `Project: ${projectName}`,
      `Audience: ${appAudience || 'Not set'}`,
      `Core promise: ${corePromise || 'Not set'}`,
      `Copy tone: ${copyTone}`,
      `Generated: ${new Date().toLocaleString()}`,
      '',
      'VIDEO EXPORT PLAN',
      `Target: ${selectedVideoPreset.label}`,
      `Resolution: ${selectedVideoPreset.width}x${selectedVideoPreset.height}`,
      `Duration: ${trimDuration.toFixed(2)} seconds`,
      'Video: H.264 High Profile Level 4.0, progressive, 30 fps, target 10-12 Mbps',
      'Audio: AAC-LC, enabled, 48 kHz, stereo, 256 kbps target',
      `Fit: ${videoFit}`,
      '',
      'SCREENSHOT EXPORT PLAN',
      `Count: ${screenshots.length}`,
      `Resolution: ${selectedScreenshotSize.width}x${selectedScreenshotSize.height}`,
      `Format: ${outputFormat.toUpperCase()}`,
      '',
      `Accuracy confirmations: real app footage ${confirmRealFootage ? 'confirmed' : 'NOT confirmed'}, screenshots/claims ${confirmClaims ? 'confirmed' : 'NOT confirmed'}`,
      '',
      'CHECKLIST',
      ...allChecks.map(check => `${check.status.toUpperCase()}: ${check.label} - ${check.detail}`),
      '',
      'VERIFY A GENERATED VIDEO WITH FFPROBE',
      'ffprobe -v error -show_entries stream=index,codec_type,codec_name,profile,width,height,r_frame_rate,sample_rate,channels,channel_layout,bit_rate:stream_disposition=default -of default=noprint_wrappers=1 YOUR_FILE.mp4',
      '',
      'Note: This report validates the configured export plan. ffprobe validates the actual generated file.',
    ];
    downloadBlob(new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' }), `${projectName.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'app'}-preflight-report.txt`);
  };

  const saveProject = async () => {
    if (userPlan !== 'indie-pro' && savedProjects.length >= 1 && !savedProjects.find(p => p.id === projectId)) {
      setPaywallReason('projects');
      setShowPaywall(true);
      return;
    }
    const project: SavedProject = {
      id: projectId,
      name: projectName.trim() || 'Untitled App',
      updatedAt: Date.now(),
      appAudience,
      corePromise,
      copyTone,
      deviceSize,
      outputFormat,
      jpgQuality,
      screenshots: screenshots.map(({ file, ...rest }) => ({ ...rest, fileName: file?.name || 'screenshot.png' })),
      videoPresetKey,
      videoFit,
      trimStart,
      trimEnd,
      posterTime,
    };
    try {
      await putSavedProject(project);
      await refreshProjects();
      setGlobalNotice('Project saved locally in this browser. Video files are not stored and must be selected again after reopening.');
    } catch (error) {
      setGlobalNotice(`Could not save project: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const loadProject = (project: SavedProject) => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    if (lastExport) URL.revokeObjectURL(lastExport.url);
    setProjectId(project.id);
    setProjectName(project.name);
    setAppAudience(project.appAudience || '');
    setCorePromise(project.corePromise || '');
    setCopyTone(project.copyTone || 'clear');
    setDeviceSize(project.deviceSize);
    setOutputFormat(project.outputFormat);
    setJpgQuality(project.jpgQuality);
    setScreenshots(project.screenshots.map(({ fileName, ...screenshot }) => ({
      ...screenshot,
      featureName: screenshot.featureName || humanizeFileName(fileName),
      benefit: screenshot.benefit || '',
      messageGoal: screenshot.messageGoal || 'benefit',
      headline: screenshot.headline || '',
      body: screenshot.body || '',
      file: new File([], fileName, { type: 'image/png' }),
    })));
    setVideoPresetKey(project.videoPresetKey);
    setVideoFit(project.videoFit);
    setTrimStart(project.trimStart);
    setTrimEnd(project.trimEnd);
    setPosterTime(project.posterTime);
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setVideoInfo(null);
    setLastExport(null);
    setSection('dashboard');
    setGlobalNotice(`Loaded ${project.name}. Choose the source video again before exporting a preview.`);
  };

  const deleteProject = async (id: string) => {
    if (!window.confirm('Delete this saved project from this browser?')) return;
    await removeSavedProject(id);
    await refreshProjects();
    setGlobalNotice('Saved project deleted.');
  };

  const createNewProject = () => {
    if (!window.confirm('Start a new project? Unsaved changes in the current project will be cleared.')) return;
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    if (lastExport) URL.revokeObjectURL(lastExport.url);
    setProjectId(`project-${Date.now()}`);
    setProjectName('My App Launch');
    setAppAudience('');
    setCorePromise('');
    setCopyTone('clear');
    setScreenshots([]);
    setDeviceSize('iphone-6.9');
    setOutputFormat('png');
    setJpgQuality(95);
    setVideoPresetKey('iphone-modern-portrait');
    setVideoFit('contain');
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setVideoInfo(null);
    setTrimStart(0);
    setTrimEnd(30);
    setPosterTime(5);
    setLastExport(null);
    setSection('dashboard');
    setGlobalNotice('New project created.');
  };

  const checkEntitlement = async (email: string): Promise<string | null> => {
    try {
      const res = await fetch(`${ENTITLEMENT_WORKER}/check?email=${encodeURIComponent(email)}`);
      const data = await res.json() as { plan: string | null };
      return data.plan || null;
    } catch { return null; }
  };

  const handleSignIn = async () => {
    if (!authEmail.trim()) { setAuthError('Please enter your email.'); return; }
    setAuthLoading(true); setAuthError('');
    const plan = await checkEntitlement(authEmail.trim().toLowerCase());
    setAuthLoading(false);
    if (plan) {
      setUserEmail(authEmail.trim().toLowerCase());
      setUserPlan(plan);
      localStorage.setItem('appready-email', authEmail.trim().toLowerCase());
      localStorage.setItem('appready-plan', plan);
      setShowAuthModal(false); setShowPaywall(false); setAuthEmail('');
      setGlobalNotice(`Welcome! Your ${plan === 'indie-pro' ? 'Indie Pro' : 'Launch Pack'} is active - watermarks removed.`);
    } else {
      setAuthError('No purchase found for this email. Check the email you used at checkout.');
    }
  };

  const handleSignOut = () => {
    setUserEmail(''); setUserPlan('');
    localStorage.removeItem('appready-email');
    localStorage.removeItem('appready-plan');
    setGlobalNotice('Signed out. Exports will include the free watermark.');
  };

  const handleUpgrade = (plan: string) => {
    const url = plan === 'indie-pro' ? CHECKOUT_URL_PRO : CHECKOUT_URL_LAUNCH;
    window.location.href = url;
  };

  const statusColor = (status: CheckStatus) => status === 'pass' ? '#15803d' : status === 'fail' ? '#b91c1c' : status === 'warn' ? '#b45309' : '#475569';
  const statusIcon = (status: CheckStatus) => status === 'pass' ? '✓' : status === 'fail' ? '×' : status === 'warn' ? '!' : 'i';

  const navItems: { key: AppSection; label: string; icon: string }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: '⌂' },
    { key: 'screenshots', label: 'Screenshots', icon: '▣' },
    { key: 'video', label: 'Preview Video', icon: '▶' },
    { key: 'preflight', label: 'Preflight', icon: '✓' },
    { key: 'projects', label: 'Projects', icon: '⌘' },
    { key: 'pricing', label: 'Pricing', icon: '★' },
  ];

  const cardStyle: React.CSSProperties = { background: '#fff', border: '1px solid #e7eaf2', borderRadius: 18, padding: 22, boxShadow: '0 8px 30px rgba(30,41,59,0.06)' };
  const primaryButton: React.CSSProperties = { border: 0, borderRadius: 10, padding: '11px 18px', background: 'linear-gradient(135deg,#5C6BC0,#3949AB)', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 14 };
  const secondaryButton: React.CSSProperties = { border: '1px solid #cbd2e2', borderRadius: 10, padding: '10px 16px', background: '#fff', color: '#334155', fontWeight: 700, cursor: 'pointer', fontSize: 14 };
  const inputStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1.5px solid #dbe0eb', borderRadius: 10, background: '#fff', fontSize: 14, outline: 'none' };

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6fb', color: '#182034', fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Helvetica Neue", Arial, sans-serif' }}>
      <style>{`
        *{box-sizing:border-box} body{margin:0} button,input,select,textarea{font:inherit}
        button:hover{filter:brightness(.98)}
        input[type=range]{-webkit-appearance:none;width:100%;height:5px;background:#dfe4ef;border-radius:3px;outline:none}
        input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:17px;height:17px;background:#5C6BC0;border-radius:50%;cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,.25)}
        .file-hidden{display:none}.upload-lbl{display:inline-flex;align-items:center;gap:8px;background:linear-gradient(135deg,#5C6BC0,#3949AB);color:#fff;border-radius:10px;padding:11px 18px;font-size:14px;font-weight:800;cursor:pointer}
        .nav-btn{width:100%;display:flex;align-items:center;gap:10px;border:0;background:transparent;color:#596174;border-radius:10px;padding:10px 12px;font-weight:700;text-align:left;cursor:pointer}.nav-btn.active{background:#eef0fb;color:#3949ab}.nav-btn:hover{background:#f1f3f8}
        .metric{background:#fff;border:1px solid #e7eaf2;border-radius:16px;padding:18px;box-shadow:0 5px 20px rgba(30,41,59,.04)}
        .grid-auto{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:16px}
        @media(max-width:850px){.app-shell{display:block!important}.sidebar{position:static!important;width:auto!important;min-height:auto!important}.sidebar nav{display:flex;overflow:auto;gap:6px}.sidebar .nav-btn{min-width:max-content}.main-content{padding:18px!important}.project-bar{flex-direction:column;align-items:stretch!important}}
      `}</style>

      <div className="app-shell" style={{ display: 'grid', gridTemplateColumns: '235px 1fr', minHeight: '100vh' }}>
        <aside className="sidebar" style={{ width: 235, background: '#fff', borderRight: '1px solid #e6e9f1', padding: 18, position: 'sticky', top: 0, height: '100vh', zIndex: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg,#5C6BC0,#3949AB)', color: '#fff', fontWeight: 900, fontSize: 19 }}>A</div>
            <div><div style={{ fontWeight: 900, fontSize: 17 }}>AppReady Studio</div><div style={{ fontSize: 11, color: '#8a93a6' }}>App Store asset preflight</div></div>
          </div>
          <nav style={{ display: 'grid', gap: 5 }}>
            {navItems.map(item => (
              <button key={item.key} className={`nav-btn ${section === item.key ? 'active' : ''}`} onClick={() => setSection(item.key)}>
                <span style={{ width: 20, textAlign: 'center' }}>{item.icon}</span>{item.label}
              </button>
            ))}
          </nav>
          <div style={{ marginTop: 22, padding: 14, borderRadius: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
            <strong>Private by design</strong><br />Screenshots and recordings stay in your browser during editing and encoding.
          </div>
          <div style={{ marginTop: 16, display: 'grid', gap: 8 }}>
            <button style={secondaryButton} onClick={saveProject}>Save project</button>
            <button style={{ ...secondaryButton, color: '#b91c1c' }} onClick={createNewProject}>New project</button>
          </div>
          <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 12, background: userPlan === 'indie-pro' ? '#eef0fb' : userPlan ? '#f0fdf4' : '#fff8f0', border: `1px solid ${userPlan === 'indie-pro' ? '#c7ccef' : userPlan ? '#bbf7d0' : '#f4d891'}` }}>
            {userPlan ? (
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, color: userPlan === 'indie-pro' ? '#3949AB' : '#15803d', marginBottom: 3 }}>
                  {userPlan === 'indie-pro' ? '★ INDIE PRO' : '✓ LAUNCH PACK'}
                </div>
                <div style={{ fontSize: 11, color: '#68728a', marginBottom: 7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userEmail}</div>
                <button onClick={handleSignOut} style={{ fontSize: 11, color: '#7b8498', background: 'transparent', border: 0, cursor: 'pointer', padding: 0 }}>Sign out</button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#92400e', marginBottom: 3 }}>FREE PLAN</div>
                <div style={{ fontSize: 10, color: '#78716c', marginBottom: 8, lineHeight: 1.5 }}>Exports include watermark. Upgrade to remove it.</div>
                <button onClick={() => setShowAuthModal(true)} style={{ width: '100%', background: '#fff', color: '#5C6BC0', border: '1px solid #c7ccef', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginBottom: 4 }}>Sign in</button>
                <button onClick={() => setSection('pricing')} style={{ width: '100%', background: 'linear-gradient(135deg,#5C6BC0,#3949AB)', color: '#fff', border: 0, borderRadius: 6, padding: '7px 10px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>Upgrade - remove watermark</button>
              </div>
            )}
          </div>
        </aside>

        <main className="main-content" style={{ padding: '26px clamp(18px,4vw,48px) 60px', minWidth: 0 }}>
          <div className="project-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 22 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, color: '#798195', fontWeight: 700, marginBottom: 5 }}>CURRENT PROJECT</div>
              <input value={projectName} onChange={e => setProjectName(e.target.value)} style={{ ...inputStyle, maxWidth: 520, fontSize: 20, fontWeight: 850, padding: '8px 10px' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setShowTour(true)} style={{ background: 'transparent', border: '1.5px solid #dde2f0', borderRadius: 8, padding: '7px 12px', fontSize: 13, color: '#5C6BC0', fontWeight: 700, cursor: 'pointer' }}>? Help</button>
              <button style={secondaryButton} onClick={() => setSection('preflight')}>Run preflight</button>
              <button style={primaryButton} onClick={saveProject}>Save</button>
            </div>
          </div>

          {globalNotice && (
            <div style={{ padding: '12px 15px', borderRadius: 11, background: '#fff8e7', border: '1px solid #f4d891', color: '#775b17', marginBottom: 18, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <span>{globalNotice}</span><button onClick={() => setGlobalNotice('')} style={{ border: 0, background: 'transparent', cursor: 'pointer', fontWeight: 900 }}>×</button>
            </div>
          )}

          {section === 'dashboard' && (
            <>
              <div style={{ marginBottom: 24 }}>
                <h1 style={{ fontSize: 31, margin: '0 0 7px', letterSpacing: -0.7 }}>Create assets Apple accepts the first time.</h1>
                <p style={{ color: '#657087', margin: 0, fontSize: 15 }}>Design screenshots, convert app previews, catch rejection-causing issues, and keep every launch organized.</p>
              </div>

              <div className="grid-auto" style={{ marginBottom: 18 }}>
                <div className="metric"><div style={{ color: '#798195', fontSize: 12, fontWeight: 800 }}>SCREENSHOTS</div><div style={{ fontSize: 30, fontWeight: 900, marginTop: 6 }}>{screenshots.length}</div><div style={{ color: '#778195', fontSize: 12 }}>of 10 App Store slots</div></div>
                <div className="metric"><div style={{ color: '#798195', fontSize: 12, fontWeight: 800 }}>VIDEO CLIP</div><div style={{ fontSize: 30, fontWeight: 900, marginTop: 6 }}>{videoInfo ? `${trimDuration.toFixed(1)}s` : 'None'}</div><div style={{ color: '#778195', fontSize: 12 }}>{selectedVideoPreset.width} × {selectedVideoPreset.height}</div></div>
                <div className="metric"><div style={{ color: '#798195', fontSize: 12, fontWeight: 800 }}>PREFLIGHT SCORE</div><div style={{ fontSize: 30, fontWeight: 900, marginTop: 6, color: failedChecks ? '#b91c1c' : '#15803d' }}>{readinessScore}%</div><div style={{ color: '#778195', fontSize: 12 }}>{failedChecks ? `${failedChecks} item${failedChecks === 1 ? '' : 's'} need attention` : 'Ready to export'}</div></div>
                <div className="metric"><div style={{ color: '#798195', fontSize: 12, fontWeight: 800 }}>SAVED PROJECTS</div><div style={{ fontSize: 30, fontWeight: 900, marginTop: 6 }}>{savedProjects.length}</div><div style={{ color: '#778195', fontSize: 12 }}>stored locally</div></div>
              </div>

              <div style={{ ...cardStyle, marginBottom: 18, background: 'linear-gradient(135deg,#eef0fb,#f8f9ff)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div><div style={{ color: '#3949ab', fontWeight: 900, fontSize: 13 }}>FASTEST PATH TO LAUNCH</div><h2 style={{ margin: '7px 0', fontSize: 23 }}>Build screenshots, export a compliant preview, then run one final check.</h2><p style={{ color: '#68728a', margin: 0 }}>No uploads to a third-party server. No blob-worker path failures. No missing AAC track.</p></div>
                  <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}><button style={primaryButton} onClick={() => setSection('screenshots')}>Create screenshots</button><button style={secondaryButton} onClick={() => setSection('video')}>Convert video</button></div>
                </div>
              </div>

              <div className="grid-auto">
                {[
                  ['Screenshot designer', 'Batch upload, device frames, typography, gradients, presets and multi-file export.'],
                  ['App preview converter', 'Trim to 15-30 seconds and export Apple-ready H.264 with a valid AAC stereo track.'],
                  ['Preflight checker', 'See duration, file size, resolution, frame rate, codec, audio and screenshot checks before upload.'],
                  ['Project library', 'Save screenshot designs and export settings locally. Your source video remains private and is never stored.'],
                ].map(([title, body]) => <div key={title} style={cardStyle}><h3 style={{ margin: '0 0 8px' }}>{title}</h3><p style={{ color: '#68728a', margin: 0, lineHeight: 1.55 }}>{body}</p></div>)}
              </div>
            </>
          )}

          {section === 'screenshots' && (
            <>
              <div style={{ marginBottom: 20 }}><h1 style={{ margin: '0 0 6px', fontSize: 28 }}>App Store screenshots</h1><p style={{ margin: 0, color: '#68728a' }}>Build app-specific messaging instead of filling every project with hard-coded example copy.</p></div>

              <div style={{ ...cardStyle, marginBottom: 18, background: 'linear-gradient(135deg,#f8f9ff,#ffffff)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'start', flexWrap: 'wrap', marginBottom: 15 }}>
                  <div><div style={{ color: '#3949ab', fontWeight: 900, fontSize: 12 }}>PROJECT MESSAGING</div><h2 style={{ margin: '5px 0 4px', fontSize: 20 }}>Tell the copy assistant what your app is about</h2><div style={{ color: '#68728a', fontSize: 13 }}>This information stays in the browser and is saved with the project.</div></div>
                  <button style={{ ...primaryButton, opacity: screenshots.length ? 1 : .5 }} disabled={!screenshots.length} onClick={generateAllScreenshotCopy}>Generate All Copy</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 13 }}>
                  <div><label style={{ fontWeight: 800, display: 'block', marginBottom: 7 }}>Target audience<Tip text="Who downloads your app? Be specific — e.g. busy parents, indie developers. This shapes the tone of generated copy." /></label><input style={inputStyle} value={appAudience} placeholder="Example: busy parents, indie developers" onChange={e => setAppAudience(e.target.value)} /></div>
                  <div><label style={{ fontWeight: 800, display: 'block', marginBottom: 7 }}>Main promise<Tip text="The single biggest benefit your app delivers. E.g. ship App Store assets without rejection. Used to generate headlines." /></label><input style={inputStyle} value={corePromise} placeholder="Example: ship App Store assets without rejection" onChange={e => setCorePromise(e.target.value)} /></div>
                  <div><label style={{ fontWeight: 800, display: 'block', marginBottom: 7 }}>Copy tone<Tip text="How should your copy sound? Clear is safe for most apps. Playful works for consumer apps. Premium suits finance apps." /></label><select style={inputStyle} value={copyTone} onChange={e => setCopyTone(e.target.value as CopyTone)}>{COPY_TONES.map(tone => <option key={tone.value} value={tone.value}>{tone.label} - {tone.description}</option>)}</select></div>
                </div>
              </div>

              <div style={{ ...cardStyle, marginBottom: 22 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px,1fr) minmax(220px,1fr)', gap: 18 }}>
                  <div><label style={{ fontWeight: 800, display: 'block', marginBottom: 8 }}>1. Upload screenshots</label><input type="file" accept="image/*" multiple onChange={handleFilesUpload} className="file-hidden" id="file-upload" /><label htmlFor="file-upload" className="upload-lbl">Choose screenshots</label><div style={{ fontSize: 12, color: '#7b8498', marginTop: 8 }}>Each screenshot starts blank. Describe the screen and generate or write its copy.</div></div>
                  <div><label style={{ fontWeight: 800, display: 'block', marginBottom: 8 }}>2. Export size</label><select value={deviceSize} onChange={e => setDeviceSize(e.target.value as DeviceKey)} style={inputStyle}><option value="iphone-6.9">iPhone 6.9 inch - 1260 × 2736</option><option value="iphone-6.5">iPhone 6.5 inch - 1284 × 2778</option><option value="ipad-13">iPad 13 inch - 2064 × 2752</option></select></div>
                  <div><label style={{ fontWeight: 800, display: 'block', marginBottom: 8 }}>3. Format</label><div style={{ display: 'flex', gap: 8 }}>{(['png','jpg'] as OutputFormat[]).map(format => <button key={format} onClick={() => setOutputFormat(format)} style={{ ...secondaryButton, background: outputFormat === format ? '#eef0fb' : '#fff', borderColor: outputFormat === format ? '#5C6BC0' : '#cbd2e2', color: outputFormat === format ? '#3949ab' : '#334155' }}>{format.toUpperCase()}</button>)}</div>{outputFormat === 'jpg' && <div style={{ marginTop: 12 }}><label style={{ fontSize: 12, color: '#68728a' }}>Quality: {jpgQuality}%</label><input type="range" min={60} max={100} value={jpgQuality} onChange={e => setJpgQuality(Number(e.target.value))} /></div>}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'flex-end' }}>
                    <button style={{ ...primaryButton, opacity: screenshots.length ? 1 : .5 }} onClick={handleBatchDownload} disabled={!screenshots.length || processing || zipExporting}>
                      {processing ? 'Exporting…' : `⬇ Download all (${screenshots.length || 0} files)`}
                    </button>
                    <button style={{ ...secondaryButton, opacity: screenshots.length ? 1 : .5 }} onClick={handleZipDownload} disabled={!screenshots.length || processing || zipExporting}>
                      {zipExporting ? 'Building ZIP…' : `📦 Download as ZIP`}
                    </button>
                  </div>
                </div>
              </div>

              {screenshots.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {screenshots.map((screenshot, index) => (
                    <ScreenshotCard
                      key={screenshot.id}
                      screenshot={screenshot}
                      index={index}
                      deviceSize={deviceSize}
                      onUpdate={updates => updateScreenshot(screenshot.id, updates)}
                      onDelete={() => deleteScreenshot(screenshot.id)}
                      onPushToAll={fields => pushToAll(screenshot.id, fields)}
                      onGenerateCopy={() => generateScreenshotCopy(screenshot.id)}
                    />
                  ))}
                </div>
              ) : (
                <div style={{ ...cardStyle, textAlign: 'center', padding: 58 }}><div style={{ fontSize: 48 }}>▣</div><h3>No screenshots yet</h3><p style={{ color: '#7b8498' }}>Select all of your raw app screenshots at once to begin.</p></div>
              )}
            </>
          )}

          {section === 'video' && (
            <>
              <div style={{ marginBottom: 20 }}><h1 style={{ margin: '0 0 6px', fontSize: 28 }}>App preview video</h1><p style={{ margin: 0, color: '#68728a' }}>Convert a screen recording into an App Store Connect-ready H.264 MP4 with a valid stereo AAC track.</p></div>
              <div style={{ ...cardStyle, marginBottom: 18 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px,1fr) minmax(280px,1fr)', gap: 24 }}>
                  <div>
                    <label style={{ fontWeight: 800, display: 'block', marginBottom: 9 }}>Source recording</label>
                    <input type="file" accept="video/mp4,video/quicktime,video/x-m4v,video/*" onChange={handleVideoUpload} className="file-hidden" id="video-upload" />
                    <label htmlFor="video-upload" className="upload-lbl">Choose video</label>
                    <div style={{ fontSize: 12, color: '#7b8498', marginTop: 8, lineHeight: 1.5 }}>Apple only accepts full-screen captures of the app. Record on a real device (Control Center, Screen Recording) or the Simulator (File, Record Screen). Don't use a framed mockup or a window capture.</div>
                    {videoInfo && <div style={{ marginTop: 12, padding: 12, background: '#f7f8fb', borderRadius: 10, fontSize: 13, lineHeight: 1.6 }}><strong>{videoFile?.name}</strong><br />{videoInfo.w} × {videoInfo.h} · {videoInfo.duration.toFixed(1)} seconds · {((videoFile?.size || 0)/1024/1024).toFixed(1)} MB</div>}
                    {videoPreviewUrl && <video src={videoPreviewUrl} controls style={{ width: '100%', maxHeight: 330, marginTop: 14, background: '#111', borderRadius: 12 }} />}
                  </div>
                  <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
                    <div><label style={{ fontWeight: 800, display: 'block', marginBottom: 7 }}>Target device</label><select value={videoPresetKey} onChange={e => setVideoPresetKey(e.target.value)} style={inputStyle}>{VIDEO_PRESETS.map(preset => <option key={preset.key} value={preset.key}>{preset.label} - {preset.width} × {preset.height}</option>)}</select><div style={{ fontSize: 12, color: '#7b8498', marginTop: 5 }}>{selectedVideoPreset.note}</div></div>
                    <div><label style={{ fontWeight: 800, display: 'block', marginBottom: 7 }}>Fit mode</label><div style={{ display: 'flex', gap: 8 }}><button style={{ ...secondaryButton, background: videoFit === 'contain' ? '#eef0fb' : '#fff', borderColor: videoFit === 'contain' ? '#5C6BC0' : '#cbd2e2' }} onClick={() => setVideoFit('contain')}>Contain, no crop</button><button style={{ ...secondaryButton, background: videoFit === 'cover' ? '#eef0fb' : '#fff', borderColor: videoFit === 'cover' ? '#5C6BC0' : '#cbd2e2' }} onClick={() => setVideoFit('cover')}>Cover, crop edges</button></div></div>
                    <div>
                      <label style={{ fontWeight: 800, display: 'block', marginBottom: 7 }}>Trim clip</label>
                      {videoInfo && videoPreviewUrl ? (
                        <>
                          <TrimScrubber
                            duration={videoInfo.duration}
                            trimStart={trimStart}
                            trimEnd={trimEnd}
                            onTrimChange={(s, e) => { setTrimStart(s); setTrimEnd(e); }}
                            videoSrc={videoPreviewUrl}
                          />
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                            <div><span style={{ fontSize: 12, color: '#68728a' }}>Start (s)</span><input type="number" min={0} max={Math.max(0, videoInfo.duration - 0.1)} step={0.1} value={trimStart.toFixed(1)} onChange={e => setTrimStart(Math.max(0, Math.min(Number(e.target.value), trimEnd - 1)))} style={inputStyle} /></div>
                            <div><span style={{ fontSize: 12, color: '#68728a' }}>End (s)</span><input type="number" min={0} max={videoInfo.duration} step={0.1} value={trimEnd.toFixed(1)} onChange={e => setTrimEnd(Math.min(videoInfo.duration, Math.max(Number(e.target.value), trimStart + 1)))} style={inputStyle} /></div>
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: 13, color: '#7b8498' }}>Choose a video to enable the trim scrubber.</div>
                      )}
                      <div style={{ fontSize: 13, fontWeight: 800, color: trimDuration >= 15 && trimDuration <= 30 ? '#15803d' : '#b91c1c', marginTop: 7 }}>{trimDuration.toFixed(1)} seconds selected. Apple requires 15 to 30 seconds.</div>
                    </div>
                    <div><label style={{ fontWeight: 800, display: 'block', marginBottom: 7 }}>Poster frame</label><div style={{ display: 'flex', gap: 9 }}><input type="number" min={0} max={videoInfo?.duration || 30} step={0.1} value={posterTime} onChange={e => setPosterTime(Number(e.target.value))} style={{ ...inputStyle, width: 110 }} /><button style={secondaryButton} onClick={capturePosterFrame}>Export poster PNG</button></div><div style={{ fontSize: 12, color: '#7b8498', marginTop: 5 }}>Apple defaults to 5 seconds. You can change the poster frame after upload.</div></div>
                  </div>
                </div>
              </div>

              <div style={{ ...cardStyle, marginBottom: 18, background: '#f8fafc' }}>
                <div className="grid-auto">
                  <div><strong>Video</strong><div style={{ color: '#68728a', fontSize: 13, marginTop: 5 }}>H.264 High Profile Level 4.0 · 30 fps · 10-12 Mbps target</div></div>
                  <div><strong>Audio</strong><div style={{ color: '#68728a', fontSize: 13, marginTop: 5 }}>AAC-LC · enabled stereo · 48 kHz · 256 kbps target</div></div>
                  <div><strong>Upload timing</strong><div style={{ color: '#68728a', fontSize: 13, marginTop: 5 }}>Transfer: seconds to minutes · Typical processing: 2-15 minutes · Apple allows up to 24 hours</div></div>
                  <div><strong>Privacy</strong><div style={{ color: '#68728a', fontSize: 13, marginTop: 5 }}>All encoding runs locally. Your unreleased recording does not leave your computer.</div></div>
                </div>
              </div>

              {videoProcessing ? (
                <div style={cardStyle}><div style={{ height: 11, background: '#e3e7ef', borderRadius: 7, overflow: 'hidden' }}><div style={{ width: `${videoProgress}%`, height: '100%', background: 'linear-gradient(90deg,#5C6BC0,#3949AB)', transition: 'width .25s' }} /></div><div style={{ marginTop: 10, fontWeight: 800 }}>{videoProgress ? `${videoProgress}%` : 'Starting encoder…'}</div><div style={{ color: '#68728a', fontSize: 13, marginTop: 5 }}>{videoStatus}</div></div>
              ) : (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}><button style={{ ...primaryButton, opacity: videoFile && trimDuration >= 15 && trimDuration <= 30 ? 1 : .5 }} disabled={!videoFile || trimDuration < 15 || trimDuration > 30} onClick={handleConvertVideo}>Export App Store-ready MP4</button>{lastExport && <><a href={lastExport.url} download={lastExport.fileName} style={{ ...secondaryButton, textDecoration: 'none', display: 'inline-block' }}>Download again</a><button style={secondaryButton} onClick={() => setSection('preflight')}>View final checklist</button></>}{videoStatus && <span style={{ color: videoStatus.startsWith('✅') ? '#15803d' : videoStatus.startsWith('⚠️') ? '#b45309' : '#b91c1c', fontWeight: 700 }}>{videoStatus}</span>}</div>
              )}

              {lastExport && <div style={{ ...cardStyle, marginTop: 18, borderColor: '#86efac', background: '#f0fdf4' }}><strong style={{ color: '#166534' }}>Latest export</strong><div style={{ marginTop: 7, color: '#365b43' }}>{lastExport.fileName} · {lastExport.width} × {lastExport.height} · {lastExport.duration.toFixed(1)}s · {(lastExport.blob.size/1024/1024).toFixed(1)} MB</div><video src={lastExport.url} controls style={{ width: '100%', maxWidth: 430, maxHeight: 420, marginTop: 12, background: '#111', borderRadius: 10 }} /></div>}
            </>
          )}

          {section === 'preflight' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'end', flexWrap: 'wrap', marginBottom: 20 }}><div><h1 style={{ margin: '0 0 6px', fontSize: 28 }}>App Store preflight</h1><p style={{ margin: 0, color: '#68728a' }}>Catch the common reasons screenshots and app previews are rejected before you upload.</p></div><button style={secondaryButton} onClick={downloadValidationReport}>Download report</button></div>
              <div style={{ ...cardStyle, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}><div style={{ width: 90, height: 90, borderRadius: '50%', display: 'grid', placeItems: 'center', background: failedChecks ? '#fef2f2' : '#f0fdf4', border: `7px solid ${failedChecks ? '#fecaca' : '#bbf7d0'}`, color: failedChecks ? '#b91c1c' : '#15803d', fontSize: 25, fontWeight: 900 }}>{readinessScore}%</div><div><h2 style={{ margin: '0 0 5px' }}>{failedChecks ? `${failedChecks} issue${failedChecks === 1 ? '' : 's'} need attention` : 'Configured for App Store Connect'}</h2><div style={{ color: '#68728a' }}>{passedChecks} checks pass. The report validates your configured export; run ffprobe on the generated MP4 to inspect its actual streams.</div></div></div>
              <div style={{ ...cardStyle, marginBottom: 18, background: '#fffbeb', borderColor: '#f4d891' }}>
                <h2 style={{ marginTop: 0, fontSize: 18 }}>Accuracy check (App Review 2.3.x)</h2>
                <p style={{ color: '#775b17', fontSize: 13, lineHeight: 1.55, margin: '0 0 12px' }}>These cannot be checked automatically. Apple rejects metadata that does not match the real app, so confirm both before you upload.</p>
                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={confirmRealFootage} onChange={e => setConfirmRealFootage(e.target.checked)} style={{ marginTop: 3 }} />
                  <span>My preview video is a full-screen recording of this version of the app, with no device frame, bezel, simulator window, background or border around it. Overlays or narration only explain what is on screen.</span>
                </label>
                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={confirmClaims} onChange={e => setConfirmClaims(e.target.checked)} style={{ marginTop: 3 }} />
                  <span>Every screenshot is from the current build, and each headline describes a feature that exists in it today.</span>
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(330px,1fr))', gap: 18 }}>
                <div style={cardStyle}><h2 style={{ marginTop: 0 }}>Preview video</h2><div style={{ display: 'grid', gap: 10 }}>{videoValidation.map(check => <div key={check.label} style={{ display: 'grid', gridTemplateColumns: '30px 1fr', gap: 10, padding: 11, border: '1px solid #e7eaf2', borderRadius: 11 }}><div style={{ width: 25, height: 25, borderRadius: '50%', display: 'grid', placeItems: 'center', background: `${statusColor(check.status)}18`, color: statusColor(check.status), fontWeight: 900 }}>{statusIcon(check.status)}</div><div><strong>{check.label}</strong><div style={{ color: '#68728a', fontSize: 12, lineHeight: 1.45, marginTop: 3 }}>{check.detail}</div></div></div>)}</div></div>
                <div style={cardStyle}><h2 style={{ marginTop: 0 }}>Screenshots</h2><div style={{ display: 'grid', gap: 10 }}>{screenshotValidation.map(check => <div key={check.label} style={{ display: 'grid', gridTemplateColumns: '30px 1fr', gap: 10, padding: 11, border: '1px solid #e7eaf2', borderRadius: 11 }}><div style={{ width: 25, height: 25, borderRadius: '50%', display: 'grid', placeItems: 'center', background: `${statusColor(check.status)}18`, color: statusColor(check.status), fontWeight: 900 }}>{statusIcon(check.status)}</div><div><strong>{check.label}</strong><div style={{ color: '#68728a', fontSize: 12, lineHeight: 1.45, marginTop: 3 }}>{check.detail}</div></div></div>)}</div></div>
              </div>
            </>
          )}

          {section === 'projects' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}><div><h1 style={{ margin: '0 0 6px', fontSize: 28 }}>Saved projects</h1><p style={{ margin: 0, color: '#68728a' }}>Stored in IndexedDB on this browser. Screenshot designs are saved; source video files are not.</p></div><button style={primaryButton} onClick={saveProject}>Save current project</button></div>
              {projectsLoading ? <div style={cardStyle}>Loading projects…</div> : savedProjects.length ? <div style={{ display: 'grid', gap: 13 }}>{savedProjects.map(project => <div key={project.id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}><div><h3 style={{ margin: '0 0 5px' }}>{project.name}</h3><div style={{ color: '#7b8498', fontSize: 13 }}>{project.screenshots.length} screenshot{project.screenshots.length === 1 ? '' : 's'} · {VIDEO_PRESETS.find(p => p.key === project.videoPresetKey)?.label || project.videoPresetKey} · updated {new Date(project.updatedAt).toLocaleString()}</div></div><div style={{ display: 'flex', gap: 8 }}><button style={secondaryButton} onClick={() => loadProject(project)}>Open</button><button style={{ ...secondaryButton, color: '#b91c1c' }} onClick={() => deleteProject(project.id)}>Delete</button></div></div>)}</div> : <div style={{ ...cardStyle, textAlign: 'center', padding: 55 }}><h3>No saved projects</h3><p style={{ color: '#7b8498' }}>Save the current project to keep its screenshot designs and export settings.</p></div>}
            </>
          )}

          {section === 'pricing' && (
            <>
              <div style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto 26px' }}><h1 style={{ margin: '0 0 8px', fontSize: 32 }}>Simple pricing for indie app launches</h1><p style={{ color: '#68728a', lineHeight: 1.6 }}>The product is usable locally today. Connect a hosted checkout URL to activate the buttons and add a backend entitlement check before public launch.</p></div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(245px,1fr))', gap: 18, alignItems: 'stretch' }}>
                <div style={cardStyle}><div style={{ fontWeight: 900, color: '#68728a' }}>FREE</div><div style={{ fontSize: 34, fontWeight: 900, margin: '10px 0' }}>$0</div><p style={{ color: '#68728a' }}>Let developers experience the workflow before paying.</p><div style={{ lineHeight: 1.9, fontSize: 14 }}>✓ 5 screenshot exports<br />✓ 1 preview conversion<br />✓ Basic preflight report<br />✓ Local browser processing</div><button style={{ ...secondaryButton, width: '100%', marginTop: 18 }} onClick={() => setSection('dashboard')}>Current beta</button></div>
                <div style={{ ...cardStyle, border: '2px solid #5C6BC0', position: 'relative' }}><div style={{ position: 'absolute', top: -12, right: 16, background: '#5C6BC0', color: '#fff', borderRadius: 20, padding: '5px 10px', fontSize: 11, fontWeight: 900 }}>BEST FOR ONE LAUNCH</div><div style={{ fontWeight: 900, color: '#3949ab' }}>LAUNCH PACK</div><div style={{ fontSize: 34, fontWeight: 900, margin: '10px 0' }}>$9</div><p style={{ color: '#68728a' }}>A low-friction purchase for one app release.</p><div style={{ lineHeight: 1.9, fontSize: 14 }}>✓ Unlimited screenshots for one app<br />✓ 3 preview video exports<br />✓ iPhone and iPad sizes<br />✓ Poster frames and reports</div><button style={{ ...secondaryButton, width: '100%', marginTop: 18 }} onClick={() => window.open(CHECKOUT_URL_LAUNCH, '_blank')}>Get Launch Pack →</button></div>
                <div style={cardStyle}><div style={{ fontWeight: 900, color: '#68728a' }}>INDIE PRO</div><div style={{ fontSize: 34, fontWeight: 900, margin: '10px 0' }}>$39<span style={{ fontSize: 15, color: '#7b8498' }}>/year</span></div><p style={{ color: '#68728a' }}>For developers shipping and updating multiple apps.</p><div style={{ lineHeight: 1.9, fontSize: 14 }}>✓ Unlimited projects and exports<br />✓ All Apple device groups<br />✓ Saved project library<br />✓ Updated validation rules</div><button style={{ ...secondaryButton, width: '100%', marginTop: 18 }} onClick={() => window.open(CHECKOUT_URL_PRO, '_blank')}>Get Indie Pro →</button></div>
              </div>

            </>
          )}
        </main>
      </div>

      {showPaywall && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 36, maxWidth: 500, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 38, marginBottom: 12 }}>{paywallReason === 'zip' || paywallReason === 'poster' || paywallReason === 'projects' ? '⭐' : '🔓'}</div>
            <h2 style={{ margin: '0 0 10px', fontSize: 22 }}>
              {paywallReason === 'zip' ? 'ZIP export - paid feature' : paywallReason === 'poster' ? 'Poster frame - paid feature' : paywallReason === 'projects' ? 'Multiple projects - Indie Pro' : 'Remove the watermark'}
            </h2>
            <p style={{ color: '#68728a', lineHeight: 1.6, margin: '0 0 22px', fontSize: 14 }}>
              {paywallReason === 'zip' ? 'ZIP download is available on Indie Pro only.' : paywallReason === 'poster' ? 'Poster frame export is available on Launch Pack and Indie Pro.' : paywallReason === 'projects' ? 'Save unlimited projects with Indie Pro.' : 'Your free export includes an AppReady Studio watermark. Upgrade to export clean, professional assets.'}
            </p>
            <div style={{ display: 'grid', gap: 10 }}>
              {paywallReason !== 'zip' && paywallReason !== 'projects' && (
                <button style={{ background: 'linear-gradient(135deg,#5C6BC0,#3949AB)', color: '#fff', border: 0, borderRadius: 10, padding: '13px 20px', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}
                  onClick={() => window.open(CHECKOUT_URL_LAUNCH, '_blank')}>Get Launch Pack — $9</button>
              )}
              <button style={{ background: paywallReason === 'zip' || paywallReason === 'projects' ? 'linear-gradient(135deg,#5C6BC0,#3949AB)' : '#f4f6fb', color: paywallReason === 'zip' || paywallReason === 'projects' ? '#fff' : '#3949AB', border: paywallReason === 'zip' || paywallReason === 'projects' ? '0' : '1.5px solid #c7ccef', borderRadius: 10, padding: '13px 20px', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}
                onClick={() => window.open(CHECKOUT_URL_PRO, '_blank')}>Get Indie Pro — $39/yr</button>
              {!userPlan && (
                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 14, marginTop: 4 }}>
                  <div style={{ fontSize: 12, color: '#68728a', marginBottom: 8 }}>Already purchased? Enter your email:</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="email" placeholder="you@example.com" value={authEmail} onChange={e => { setAuthEmail(e.target.value); setAuthError(''); }} onKeyDown={e => e.key === 'Enter' && handleSignIn()} style={{ flex: 1, padding: '10px 12px', border: '1.5px solid #dbe0eb', borderRadius: 8, fontSize: 14, outline: 'none' }} />
                    <button onClick={handleSignIn} disabled={authLoading} style={{ background: '#5C6BC0', color: '#fff', border: 0, borderRadius: 8, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 14, opacity: authLoading ? 0.6 : 1 }}>{authLoading ? '…' : 'Unlock'}</button>
                  </div>
                  {authError && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>{authError}</div>}
                </div>
              )}
              <button onClick={() => setShowPaywall(false)} style={{ background: 'transparent', color: '#7b8498', border: 0, padding: '8px', fontSize: 13, cursor: 'pointer' }}>Continue with watermark</button>
            </div>
          </div>
        </div>
      )}
      {showAuthModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 36, maxWidth: 420, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔓</div>
            <h2 style={{ margin: '0 0 8px', fontSize: 22 }}>Remove the watermark</h2>
            <p style={{ color: '#68728a', margin: '0 0 22px', lineHeight: 1.5, fontSize: 14 }}>Enter the email you used at checkout to verify your purchase and remove the watermark instantly.</p>
            <input type="email" placeholder="you@example.com" value={authEmail} autoFocus onChange={e => { setAuthEmail(e.target.value); setAuthError(''); }} onKeyDown={e => e.key === 'Enter' && handleSignIn()} style={{ width: '100%', boxSizing: 'border-box' as const, padding: '12px 14px', border: '1.5px solid #dbe0eb', borderRadius: 10, fontSize: 15, outline: 'none', marginBottom: 10 }} />
            {authError && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{authError}</div>}
            <div style={{ display: 'grid', gap: 8 }}>
              <button onClick={handleSignIn} disabled={authLoading} style={{ background: 'linear-gradient(135deg,#5C6BC0,#3949AB)', color: '#fff', border: 0, borderRadius: 10, padding: '13px 20px', fontWeight: 800, fontSize: 15, cursor: 'pointer', opacity: authLoading ? 0.6 : 1 }}>{authLoading ? 'Checking…' : 'Remove watermark'}</button>
              <button onClick={() => { setShowAuthModal(false); setAuthError(''); setAuthEmail(''); }} style={{ background: 'transparent', color: '#7b8498', border: 0, padding: '10px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            </div>
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #f0f0f0', fontSize: 13, color: '#68728a' }}>
              Don't have a plan?{' '}<button onClick={() => { setShowAuthModal(false); setSection('pricing'); }} style={{ color: '#5C6BC0', background: 'none', border: 0, cursor: 'pointer', fontWeight: 700, fontSize: 13, padding: 0 }}>View pricing →</button>
            </div>
          </div>
        </div>
      )}
            {showTour && <Walkthrough onClose={() => setShowTour(false)} onNavigate={(s) => { setSection(s as AppSection); }} />}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}

export default App;